/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";
import type { Dispatch, PayloadAction } from "@reduxjs/toolkit";
import type { MyHubFilterList, StorageInfo } from "client-code/myshows";

import type {
  TAnalyticsFields,
  TCarouselItem,
  TMyHubActionType,
  TMyHubBulkActionType,
  TMyHubCarousel,
  TMyHubCarouselList,
  TMyHubFolderDetail,
  TMyHubItemUnion,
  TRecordingMyHubItem
} from "../../model/types";
import { DiagnosticSeverity } from "../../model/types";
import {
  MyShowsInteractor,
  OnePassInteractor,
  RecordingsServiceInteractor
} from "../../model/interactor/shared";
import { ErrorTypes } from "../../model/error";
import { Locale, LocaleKeys } from "../../model/locale";
import { AnalyticsLogger, DiagnosticLogger, Log } from "../../model/logging";
import { getImageHostBaseUrl } from "../utility/smartCarouselUtility";
import { selectPreviousUrl } from "../screens";
import { reportMyHubScreenFailure } from "./myHubScreenErrorDialog";
import { showToastMessage } from "../toastMessage";
import {
  getMyHubErrorMessage,
  getMyHubErrorToastMessage,
  logMyHubApiFailure
} from "./myHubErrorMapper";
import {
  applyOffsetPage,
  CAROUSEL_TILE_PAGE_SIZE
} from "../utility/carouselPagination";
import type {
  TTilePage,
  TTilePaginationState
} from "../utility/carouselPagination";
import { createLoadMoreTilesThunk } from "../utility/carouselThunkFactory";
import type { TLoadMoreTilesPayload } from "../utility/carouselThunkFactory";
import {
  MY_HUB_SORT_OPTIONS,
  isMyHubFlatListFilter,
  toMyHubFilterLayout
} from "./myHubFilters";
import type { TMyHubFilterMeta } from "./myHubFilters";
import {
  MY_HUB_ORDER_BY,
  MY_HUB_FILTER_STRIP_LIST_NAME,
  MY_HUB_SCREEN_ID,
  MY_HUB_SCREEN_NAME,
  MY_HUB_SORT_CONTROL_LIST_NAME,
  MyHubFilterId,
  MyHubFocusRegion,
  MyHubSortId
} from "./myHub.types";
import {
  mapMyHubCarousel,
  mapMyHubCarouselToTilePage,
  mapMyHubCarouselsToInitialTilePagination,
  resolveChannelNames,
  toCallSignResolver
} from "./myHubMappers";
import { mapMyHubSavedList, mapMyHubSavedPage } from "./myHubSavedMappers";
import {
  mapMyHubFolderDetail,
  mapScheduledSingleRecording
} from "./myHubSeriesMappers";
import type {
  MyHubState,
  TMyHubCarouselPaginationState,
  TMyHubRecordingQuota,
  TMyHubCarouselData,
  TMyHubSavedItem,
  TMyHubSavedListSummary,
  TMyHubSeriesSummary
} from "./myHub.types";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import {
  moveCarouselTileLeft,
  moveCarouselTileRight,
  moveCarouselRowUp,
  moveCarouselRowDown
} from "../../model/navigation";

const TAG: string = "myHubSlice";

/** DiagnosticLogger category for every My Hub lifecycle/failure event — mirrors ContentDetail's "content_detail_lifecycle". */
const DIAGNOSTIC_CATEGORY: string = "my_hub_lifecycle";

/**
 * Reports a failed My Hub call that surfaces as a toast: logs it to the
 * operational logger under `code`, then toasts the copy carrying that code.
 *
 * @param dispatch - The failing thunk's dispatch
 * @param error - The raw error the call threw
 * @param code - The call's `ErrorTypes` catalog code
 * @param operation - The failing thunk, to tell My Hub's endpoints apart
 */
function reportMyHubActionFailure(
  dispatch: Dispatch,
  error: unknown,
  code: string,
  operation: string
): void {
  logMyHubApiFailure(error, code, operation);
  dispatch(showToastMessage(getMyHubErrorToastMessage(error, code)));
}

// ── Analytics loggers ────────────────────────────────────────────────────────
//
// Built lazily and cached, never at module scope: `AnalyticsLogger.create()`
// returns a fresh anonymous subclass per call, so creating one eagerly makes
// importing this slice a side effect — which is what made any consumer that
// partially mocks `model/logging` blow up on import — and creating one per
// call site allocates a new class (and a new identity) on every keypress.
// There are only ever five My Hub screens, so there are only ever five loggers.

const tabAnalyticsLoggers = new Map<MyHubFilterId, typeof AnalyticsLogger>();

/**
 * Screen-scoped analytics logger for one My Hub tab. Exported so the view
 * layer can log tile-focus (`contentHighlighted`) events with the same
 * per-tab screen identity the fetch thunks use for `viewedScreen`.
 *
 * Stable across calls: `myHubTabAnalyticsLogger(SAVED)` is the same object
 * every time, so it is safe in a dep array and assertable in a test.
 */
export function myHubTabAnalyticsLogger(
  filter: MyHubFilterId
): typeof AnalyticsLogger {
  const cached: typeof AnalyticsLogger | undefined =
    tabAnalyticsLoggers.get(filter);
  if (cached !== undefined) {
    return cached;
  }
  const logger: typeof AnalyticsLogger = AnalyticsLogger.create({
    screenId: MY_HUB_SCREEN_ID,
    screenName: MY_HUB_SCREEN_NAME
  });
  tabAnalyticsLoggers.set(filter, logger);
  return logger;
}

let manageSeriesLogger: typeof AnalyticsLogger | null = null;

/**
 * Analytics logger for the series/OnePass management (folder-detail) screen —
 * see {@link myHubTabAnalyticsLogger} for why this is a function rather than a
 * module-scope constant.
 */
export function manageSeriesAnalyticsLogger(): typeof AnalyticsLogger {
  if (manageSeriesLogger === null) {
    manageSeriesLogger = AnalyticsLogger.create({
      screenId: MY_HUB_SCREEN_ID,
      screenName: MY_HUB_SCREEN_NAME
    });
  }
  return manageSeriesLogger;
}

// ── viewedScreen timing ──────────────────────────────────────────────────────
//
// `viewedScreen` fires once per screen *visit*, on exit, so it can report
// `previous_screen_id` (where the visit started from) and `time_on_screen`
// (how long it lasted) — mirrors ContentDetail's own entry-capture/exit-fire
// pair (`captureContentDetailEntryScreen`/`logContentDetailViewedScreen`).
// My Hub has two such "screens": the active tab (Recorded/Scheduled/Saved/
// Deleted) and the manage-series/scheduled drill-in, each tracked separately
// since a user can visit one, then the other, in the same mount.

/** previous_screen_id + entry time for the currently active My Hub tab. */
let myHubTabEntryScreenId: string = "";
let myHubTabEntryTimestamp: number = 0;

/** previous_screen_id + entry time for the manage-series/scheduled drill-in. */
let manageSeriesEntryScreenId: string = "";
let manageSeriesEntryTimestamp: number = 0;

/**
 * Captures the entry screen for the active My Hub tab; dispatch on
 * `MyHubScreen` mount, before any tab switch has a chance to fire.
 */
export const captureMyHubEntryScreen = createAsyncThunk<
  void,
  void,
  { state: UnityRootState }
>("myHub/captureMyHubEntryScreen", (_: void, { getState }): void => {
  myHubTabEntryScreenId = selectPreviousUrl(getState());
  myHubTabEntryTimestamp = Date.now();
  Log.d(
    TAG,
    `captureMyHubEntryScreen() — entered from ${myHubTabEntryScreenId}`
  );
  DiagnosticLogger.log(
    DIAGNOSTIC_CATEGORY,
    "captureMyHubEntryScreen() — screen mounted",
    DiagnosticSeverity.Debug,
    { event: "screen_mount", previous_screen_id: myHubTabEntryScreenId }
  );
});

/**
 * Fires `viewedScreen` for the tab being left because the user switched to
 * another one, then arms the entry state for whichever tab comes next — its
 * `previous_screen_id` becomes the tab just left, matching how a real
 * screen-to-screen navigation reports it.
 */
function logMyHubTabExit(filter: MyHubFilterId): void {
  if (myHubTabEntryTimestamp === 0) {
    return;
  }
  myHubTabAnalyticsLogger(filter).logViewedScreen({
    previous_screen_id: myHubTabEntryScreenId,
    time_on_screen: String(Date.now() - myHubTabEntryTimestamp)
  });
  myHubTabEntryScreenId = MY_HUB_SCREEN_ID;
  myHubTabEntryTimestamp = Date.now();
}

/**
 * Fires `viewedScreen` for the tab being left because the user left My Hub
 * entirely, and clears the entry state rather than arming it for a next tab —
 * a fresh mount re-captures it via {@link captureMyHubEntryScreen}.
 */
function logMyHubScreenExit(filter: MyHubFilterId): void {
  if (myHubTabEntryTimestamp === 0) {
    return;
  }
  myHubTabAnalyticsLogger(filter).logViewedScreen({
    previous_screen_id: myHubTabEntryScreenId,
    time_on_screen: String(Date.now() - myHubTabEntryTimestamp)
  });
  myHubTabEntryScreenId = "";
  myHubTabEntryTimestamp = 0;
}

/**
 * `itemSelected` for a tab change, plus the `viewedScreen` that closes out the
 * tab being left — the shared body behind {@link focusMyHubFilterChip} and
 * {@link applyMyHubFilter}, which differ only in how the chip was picked.
 */
function logMyHubFilterSelected(
  previousFilter: MyHubFilterId,
  nextFilter: MyHubFilterId
): void {
  myHubTabAnalyticsLogger(previousFilter).logItemSelected({
    list_name: MY_HUB_FILTER_STRIP_LIST_NAME,
    tile_id: nextFilter,
    previous_screen_id: MY_HUB_SCREEN_ID
  });
  logMyHubTabExit(previousFilter);
}

/**
 * `itemSelected` for a sort change — the shared body behind
 * {@link focusMyHubSortChip} and {@link applyMyHubSort}. No `viewedScreen`:
 * sorting re-orders the tab, it does not leave it.
 */
function logMyHubSortSelected(
  filter: MyHubFilterId,
  nextSort: MyHubSortId
): void {
  myHubTabAnalyticsLogger(filter).logItemSelected({
    list_name: MY_HUB_SORT_CONTROL_LIST_NAME,
    tile_id: nextSort
  });
}

/** Fires `viewedScreen` for the manage-series/scheduled drill-in being left. */
function logManageSeriesExit(): void {
  if (manageSeriesEntryTimestamp === 0) {
    return;
  }
  manageSeriesAnalyticsLogger().logViewedScreen({
    previous_screen_id: manageSeriesEntryScreenId,
    time_on_screen: String(Date.now() - manageSeriesEntryTimestamp)
  });
  manageSeriesEntryScreenId = "";
  manageSeriesEntryTimestamp = 0;
}

/**
 * Rows requested per `GET /v1/myHub/carousels` grid load.
 *
 * Matches the Home screen's `CAROUSEL_PAGE_SIZE` (`homescreenConstants.ts`) —
 * kept as a local constant rather than importing that one, since it is not
 * documented as shared across screens the way `CAROUSEL_TILE_PAGE_SIZE` is.
 */
const MY_HUB_CAROUSEL_PAGE_SIZE: number = 3;

/**
 * Whether another page of carousel ROWS can be requested for the active
 * filter/tab. `totalCount === null` means the first response hasn't arrived
 * yet, so pagination keeps going until it says otherwise; once it has, this
 * stops as soon as every reported row has loaded.
 */
export const canLoadMoreMyHubCarousels = (
  state: TMyHubCarouselPaginationState
): boolean => {
  const hasMore: boolean =
    state.totalCount === null || state.items.length < state.totalCount;
  return hasMore && !state.isLoading;
};

// ── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Clamps `index` to the valid range [0, length - 1].
 * Returns 0 when `length` is 0 or less.
 */
const clampIndex = (index: number, length: number): number => {
  if (length <= 0) {
    return 0;
  }
  if (index < 0) {
    return 0;
  }
  if (index >= length) {
    return length - 1;
  }
  return index;
};

/**
 * Helper: Moves focus from top bars (Filters/Sort) to the first carousel.
 */
const moveToFirstCarousel = (
  state: MyHubState,
  carouselItemCounts: number[]
): void => {
  if (carouselItemCounts.length > 0) {
    state.focusRegion = MyHubFocusRegion.CAROUSEL_ITEMS;
    state.focusedCarouselIndex = 0;
    state.focusedItemIndex = clampIndex(
      state.focusedItemIndex,
      carouselItemCounts[0]
    );
  }
};

/**
 * Helper: Returns focus to the filter row, highlighting the chip that matches
 * the active filter rather than whichever chip was highlighted last — pressing
 * UP out of the content area should land on the tab the user is looking at.
 */
const returnFocusToFilters = (state: MyHubState): void => {
  state.focusRegion = MyHubFocusRegion.FILTERS;
  const selectedFilterIndex: number = state.filters.items.findIndex(
    (opt: TMyHubFilterMeta): boolean => opt.id === state.selectedFilter
  );
  state.focusedFilterIndex = Math.max(0, selectedFilterIndex);
};

/**
 * Helper: Moves focus onto the Sort By control, highlighting the segment that
 * matches the active sort order rather than whichever segment was highlighted
 * last — the twin of {@link returnFocusToFilters}, and for the same reason:
 * arriving on Sort By should point at the order currently in force.
 *
 * Shared by both screens that carry the control, so the cursor cannot land on
 * one segment on My Hub and another on manage-series.
 */
const focusSortControl = (state: MyHubState): void => {
  state.focusRegion = MyHubFocusRegion.SORT;
  const selectedSortIndex: number = MY_HUB_SORT_OPTIONS.findIndex(
    (opt: TMyHubSortMeta): boolean => opt.id === state.selectedSort
  );
  state.focusedSortIndex = Math.max(0, selectedSortIndex);
};

/**
 * Clears the previous filter's carousel/flat-list content, resets tile
 * focus and puts the sort back to Recent, for use whenever the active filter
 * changes.
 *
 * Without this, the outgoing tab's rows would stay on screen — and stay
 * navigable — until the new fetch resolves, since nothing else clears
 * `carouselsPagination` / `savedList` on a filter switch.
 */
const clearMyHubContentOnFilterChange = (state: MyHubState): void => {
  state.carouselsPagination.items = [];
  state.carouselsPagination.totalCount = null;
  state.carouselsPagination.error = "";
  state.tilePagination = {};
  state.savedList.items = [];
  state.savedList.summary = null;
  state.savedList.carouselId = null;
  state.savedList.resolvedCount = null;
  state.savedList.error = "";
  state.focusedCarouselIndex = 0;
  state.focusedItemIndex = 0;
  state.carouselItemCounts = [];
  state.focusedSavedIndex = 0;
  state.focusedSavedActionIndex = 0;
  state.selectedSort = MyHubSortId.RECENT;
};

/**
 * Number of action buttons on the Saved row at `itemIndex`.
 *
 * The Saved list's LEFT/RIGHT bounds come from the focused row's own action set
 * rather than from a count synced by the view, because the rows live in this
 * slice — unlike the carousels, which still need `carouselItemCounts`.
 *
 * @returns The action count, or 0 when the index is out of range.
 */
const savedActionCount = (state: MyHubState, itemIndex: number): number => {
  const item: TMyHubSavedItem | undefined = state.savedList.items[itemIndex];
  return item === undefined ? 0 : item.availableActions.length;
};

/**
 * Moves Saved-list focus to `itemIndex`, clamped, and clamps the action column
 * to `actionIndex` (0 by default).
 *
 * A real row change defaults the column to 0: rows do not all offer the same
 * actions, so carrying a stale index across rows would land focus on an
 * unrelated button or out of bounds entirely. A caller re-clamping the same
 * row after its own list refetches underneath it — e.g. a sticky return's
 * refresh — passes the current column through instead, so that refresh does
 * not visibly reset which button was focused.
 */
const moveToSavedItem = (
  state: MyHubState,
  itemIndex: number,
  actionIndex: number = 0
): void => {
  state.focusedSavedIndex = clampIndex(itemIndex, state.savedList.items.length);
  state.focusedSavedActionIndex = clampIndex(
    actionIndex,
    savedActionCount(state, state.focusedSavedIndex)
  );
};

/**
 * Whether the active flat-list tab offers a bulk button above its rows.
 *
 * Server-driven rather than hardcoded per tab: the button exists because the
 * response's `header.bulkAction` says so ("permanentlyDeleteAll" on the Deleted
 * tab), so the vertical focus order has to be derived the same way instead of
 * assuming the Deleted tab always has one.
 */
const hasBulkAction = (state: MyHubState): boolean => {
  return (
    state.savedList.summary !== null &&
    state.savedList.summary.bulkAction !== null
  );
};

/**
 * Enters the flat list's content area from the tab strip above it.
 *
 * The bulk button sits between the two (Figma node 71:2376 places it on the
 * summary line), so it takes focus first when the tab has one and DOWN steps on
 * into the rows from there.
 */
const moveIntoFlatList = (state: MyHubState): void => {
  if (hasBulkAction(state)) {
    state.focusRegion = MyHubFocusRegion.BULK_ACTION;
    return;
  }
  if (state.savedList.items.length > 0) {
    state.focusRegion = MyHubFocusRegion.SAVED_LIST;
    moveToSavedItem(state, 0);
  }
};

/**
 * Hands focus back out of a flat-list stop that the refresh has taken away —
 * an emptied row list, or a bulk button the tab no longer offers.
 *
 * A row can be the last one: unsave it, or permanently delete it, and the
 * refresh `performMyHubAction` runs comes back empty — while the cursor is
 * still in `SAVED_LIST` (the refresh even puts it there, see
 * `refreshMyHubAfterAction`). Nothing then carries the focus ring, and the
 * cursor is not merely invisible but stuck: DOWN clamps onto a row that does
 * not exist, LEFT/RIGHT walk an empty action row, and OK resolves no action.
 * Only UP would recover it, which is not something a user can be expected to
 * guess.
 *
 * Exits the way {@link moveIntoFlatList} enters, in reverse, so the cursor
 * lands where DOWN would have brought it from: the bulk button when the tab has
 * one, otherwise the tab strip.
 */
const leaveEmptyFlatList = (state: MyHubState): void => {
  // The bulk button is itself something that can go away under the cursor:
  // "Permanently Delete All" empties the tab it belongs to, and the header the
  // refresh comes back with no longer declares one. Focus then has nothing to
  // sit on, so it goes back up to the tab strip — onto the chip of the tab
  // still selected, which is where the press came from.
  if (state.focusRegion === MyHubFocusRegion.BULK_ACTION) {
    if (!hasBulkAction(state)) {
      returnFocusToFilters(state);
    }
    return;
  }
  if (
    state.focusRegion !== MyHubFocusRegion.SAVED_LIST ||
    state.savedList.items.length > 0
  ) {
    return;
  }
  if (hasBulkAction(state)) {
    state.focusRegion = MyHubFocusRegion.BULK_ACTION;
    return;
  }
  returnFocusToFilters(state);
};

// ── Manage-series focus helpers ──────────────────────────────────────────────
// The manage-series screen (Figma node 71:4516) renders the same flat card list
// as the Saved / Deleted tabs and reuses their row focus fields, but its chrome
// is different: a row of folder buttons where the tab strip sits, and no bulk
// button. That is why it navigates through its own reducers rather than the
// landing screen's — everything below is scoped to `state.series`.

/**
 * Number of action buttons on the manage-series row at `itemIndex`.
 *
 * The series twin of {@link savedActionCount}, reading the folder's rows instead
 * of the tab's, for the same reason: LEFT/RIGHT bounds come from the focused
 * row's own action set.
 *
 * @returns The action count, or 0 when the index is out of range.
 */
const seriesActionCount = (state: MyHubState, itemIndex: number): number => {
  const item: TMyHubSavedItem | undefined = state.series.items[itemIndex];
  return item === undefined ? 0 : item.availableActions.length;
};

/**
 * Moves manage-series row focus to `itemIndex`, clamped, and clamps the action
 * column to `actionIndex` (0 by default). Series twin of {@link moveToSavedItem}.
 */
const moveToSeriesItem = (
  state: MyHubState,
  itemIndex: number,
  actionIndex: number = 0
): void => {
  state.focusedSavedIndex = clampIndex(itemIndex, state.series.items.length);
  state.focusedSavedActionIndex = clampIndex(
    actionIndex,
    seriesActionCount(state, state.focusedSavedIndex)
  );
};

/**
 * Number of folder buttons in the manage-series header.
 *
 * Server-driven, exactly as the flat list's bulk button is: the response's
 * `folderActions` decides how many buttons exist, so the row's LEFT/RIGHT bounds
 * are derived from it rather than from a hardcoded three.
 */
const seriesActionButtonCount = (state: MyHubState): number => {
  const summary: TMyHubSeriesSummary | null = state.series.summary;
  return summary === null ? 0 : summary.folderActions.length;
};

/**
 * Enters the manage-series rows from the chrome above them.
 *
 * Unlike {@link moveIntoFlatList} there is no intermediate control to stop at —
 * the folder buttons are the chrome, so DOWN out of them lands on the first row.
 */
const moveIntoSeriesList = (state: MyHubState): void => {
  if (state.series.items.length > 0) {
    state.focusRegion = MyHubFocusRegion.SAVED_LIST;
    moveToSeriesItem(state, 0);
  }
};

/**
 * Series twin of {@link leaveEmptyFlatList}: hands focus back to the chrome when
 * a refresh of the open folder returns no rows.
 *
 * The folder can empty out from this screen — Delete on its last episode, or
 * Delete All Recordings — and the refetch that follows lands while the cursor is
 * still on a row. Exits the way {@link moveIntoSeriesList} enters, in reverse:
 * onto the folder buttons when the response still offers any, otherwise onto the
 * sort control, which is the only other stop on that line.
 */
const leaveEmptySeriesList = (state: MyHubState): void => {
  if (
    state.focusRegion !== MyHubFocusRegion.SAVED_LIST ||
    state.series.items.length > 0
  ) {
    return;
  }
  const buttonCount: number = seriesActionButtonCount(state);
  if (buttonCount > 0) {
    state.focusRegion = MyHubFocusRegion.SERIES_ACTIONS;
    state.focusedSeriesActionIndex = clampIndex(
      state.focusedSeriesActionIndex,
      buttonCount
    );
    return;
  }
  focusSortControl(state);
};

/**
 * Maps a `GET /v1/diskSpace` (cloudcore-myshows) response onto the header
 * quota.
 *
 */
export const toRecordingQuota = (
  storageInfo: StorageInfo
): TMyHubRecordingQuota => {
  const freeMinutes: number = Number.isFinite(
    storageInfo.availableStorageMinutes
  )
    ? Math.max(0, storageInfo.availableStorageMinutes)
    : 0;

  const totalMinutes: number =
    Number.isFinite(storageInfo.totalStorageMinutes) &&
    storageInfo.totalStorageMinutes >= freeMinutes
      ? storageInfo.totalStorageMinutes
      : freeMinutes;

  return { freeMinutes, totalMinutes };
};

/**
 * Resolves the image host the tile art is built against.
 *
 * A failure here is not fatal: the mappers emit an empty `imageUrl`, and the
 * cards fall back to their title stand-in, so the screen still renders. Shared
 * by both fetch thunks rather than duplicated.
 *
 * @param caller - Thunk name, for the log line only.
 * @returns The base URL, or `""` when it could not be resolved.
 */
const resolveImageHostBaseUrl = async (caller: string): Promise<string> => {
  const interactor: MyShowsInteractor = new MyShowsInteractor();
  try {
    const baseUrl: string = await getImageHostBaseUrl(
      interactor as unknown as Parameters<typeof getImageHostBaseUrl>[0]
    );
    Log.d(TAG, `${caller}() — imageHostBaseUrl=${baseUrl}`);
    return baseUrl;
  } catch (error: unknown) {
    Log.w(TAG, `${caller}() — failed to fetch imageHostBaseUrl`, error);
    return "";
  }
};

/**
 * What {@link fetchMyHubCarousels} and {@link loadMoreMyHubCarousels} each
 * resolve to: one page of mapped rows, the total row count across the whole
 * grid (`totalCount` — from the response's X-Total-Count header, distinct
 * from each row's own tile-count `resolvedCount`), and the per-row offset
 * tile pagination map seeded from those rows — mirrors Home Screen's
 * `InitialCarouselsPayload` (`homescreenSlice.ts`).
 */
interface TMyHubCarouselsPagePayload {
  carousels: TMyHubCarouselData[];
  tilePagination: Record<string, TTilePaginationState>;
  totalCount: number;
}

/**
 * Fetches the next tile page for one My Hub carousel row using offset/count
 * paging, for {@link loadMoreMyHubTiles}'s `fetchPage` — the My Hub twin of
 * `fetchCarouselTilePage` in `smartCarouselUtility.ts`.
 *
 * `myHubFilter`/`orderBy` are omitted: `carouselId` already scopes the
 * request to the one row the initial grid load returned, and both params are
 * optional on `TMyHubFetchCarouselParams`.
 *
 * Exported for {@link loadMoreSeeMoreItems} (`seeMoreThunks.ts`): a My Hub row's
 * See More grid must page through this same MyShows-backed carousel, not the
 * smart-carousel endpoint `fetchCarouselTilePage` calls — My Hub's carousel IDs
 * are not smart-carousel IDs, so that call 404s / returns the wrong content.
 */
export const fetchMyHubCarouselTilePage = async (
  carouselId: string,
  offset: number,
  count: number
): Promise<TTilePage> => {
  const imageHostBaseUrl: string = await resolveImageHostBaseUrl(
    "fetchMyHubCarouselTilePage"
  );
  const interactor: MyShowsInteractor = new MyShowsInteractor();
  const carousel: TMyHubCarousel = (await interactor.fetchMyHubCarousel({
    carouselId,
    offset,
    limit: count
  })) as unknown as TMyHubCarousel;
  return mapMyHubCarouselToTilePage(carousel, imageHostBaseUrl);
};

// ── Async thunks ──────────────────────────────────────────────────────────────

/**
 * Fetches the subscriber's DVR storage via {@link MyShowsInteractor} and maps
 * it onto the header quota shape.
 *
 * Dispatch once per My Hub mount — the quota spans the whole DVR, so it does
 * not need refetching when the filter or sort changes.
 */
export const fetchMyHubRecordingQuota = createAsyncThunk<
  TMyHubRecordingQuota,
  void,
  { rejectValue: string }
>("myHub/fetchMyHubRecordingQuota", async (_: void, { rejectWithValue }) => {
  try {
    Log.i(TAG, "fetchMyHubRecordingQuota() — fetching DVR disk space");
    const interactor: MyShowsInteractor = new MyShowsInteractor();
    const storageInfo: StorageInfo = await interactor.fetchDiskSpace();
    const quota: TMyHubRecordingQuota = toRecordingQuota(storageInfo);
    Log.d(
      TAG,
      `fetchMyHubRecordingQuota() SUCCESS — free=${quota.freeMinutes}min total=${quota.totalMinutes}min`
    );
    DiagnosticLogger.log(
      DIAGNOSTIC_CATEGORY,
      "fetchMyHubRecordingQuota() succeeded",
      DiagnosticSeverity.Debug,
      {
        event: "recording_quota_loaded",
        free_minutes: String(quota.freeMinutes),
        total_minutes: String(quota.totalMinutes)
      }
    );
    return quota;
  } catch (error: unknown) {
    Log.e(TAG, "fetchMyHubRecordingQuota()", error);
    // R505 is reported only — the header just drops its storage readout
    // (see the rejected reducer), so there is no toast to raise.
    logMyHubApiFailure(
      error,
      ErrorTypes.MY_HUB_STORAGE_REQUEST_FAILED_R505,
      "fetchMyHubRecordingQuota"
    );
    return rejectWithValue(
      getMyHubErrorMessage(error, ErrorTypes.MY_HUB_STORAGE_REQUEST_FAILED_R505)
    );
  }
});

/**
 * Fetches the My Hub filter/tab strip via {@link MyShowsInteractor} and maps
 * each `{ value, label, layout }` triple onto a {@link MyHubFilterId}.
 *
 * A fetched `value` that does not match a known `MyHubFilterId` is dropped: the
 * rest of the slice identifies filters by that enum, not by an arbitrary
 * server string, so an unrecognized tab would have nowhere to route to. An
 * unrecognized `layout` is dropped the same way, leaving the chip without one —
 * see {@link isMyHubFlatListFilter} for what that falls back to.
 *
 * Dispatch once per My Hub mount — the strip itself does not depend on the
 * active filter or sort.
 */
export const fetchMyHubFilters = createAsyncThunk<
  TMyHubFilterMeta[],
  void,
  { rejectValue: string }
>("myHub/fetchMyHubFilters", async (_: void, { dispatch, rejectWithValue }) => {
  try {
    Log.i(TAG, "fetchMyHubFilters() — fetching filter/tab strip");
    const interactor: MyShowsInteractor = new MyShowsInteractor();
    const response: MyHubFilterList = await interactor.fetchMyHubFilters();
    const knownFilterIds: string[] = Object.values(MyHubFilterId);
    const filters: TMyHubFilterMeta[] = response.myHubFilters
      .filter((item): boolean => knownFilterIds.includes(item.value))
      .map(
        (item): TMyHubFilterMeta => ({
          id: item.value as MyHubFilterId,
          label: item.label,
          layout: toMyHubFilterLayout(item.layout)
        })
      );
    Log.d(TAG, `fetchMyHubFilters() SUCCESS — count=${filters.length}`);
    DiagnosticLogger.log(
      DIAGNOSTIC_CATEGORY,
      "fetchMyHubFilters() succeeded",
      DiagnosticSeverity.Debug,
      { event: "filters_loaded", count: String(filters.length) }
    );
    return filters;
  } catch (error: unknown) {
    Log.e(TAG, "fetchMyHubFilters()", error);
    reportMyHubActionFailure(
      dispatch,
      error,
      ErrorTypes.MY_SHOWS_FILTER_ERROR_A755,
      "fetchMyHubFilters"
    );
    return rejectWithValue(
      getMyHubErrorMessage(error, ErrorTypes.MY_SHOWS_FILTER_ERROR_A755)
    );
  }
});

/**
 * Fetches one page of My Hub carousel ROWS from the API and maps them to
 * `TCarouselItem` tiles — the shared body behind both {@link fetchMyHubCarousels}
 * (offset 0, replaces the grid) and {@link loadMoreMyHubCarousels} (offset =
 * rows already loaded, appends).
 *
 * Wired to `GET /v1/myHub/carousels` (cloudcore-myshows 0.1.0) via
 * `MyShowsInteractor.fetchMyHubCarousels`. The interactor returns the generated
 * `MyHubCarouselList` (widened with the header-derived `totalCount`), whose
 * `.carousels` is cast to the hand-written `TMyHubCarouselList` — see the
 * header comment in `model/types/TMyHubDto.ts` for why the two are kept as
 * separate, deliberately identical types rather than one shared import.
 *
 * `MY_HUB_ORDER_BY[selectedSort]` translates the sort chip id into the wire
 * `orderBy` value — the chip ids themselves are not wire values.
 */
const fetchMyHubCarouselsPage = async (
  getState: () => UnityRootState,
  offset: number
): Promise<TMyHubCarouselsPagePayload> => {
  // The tab is read from state rather than passed in, so every caller stays a
  // bare dispatch() and cannot desync the request from the visible tab.
  const filter: MyHubFilterId = getState().myHub.selectedFilter;

  const imageHostBaseUrl: string = await resolveImageHostBaseUrl(
    "fetchMyHubCarouselsPage"
  );

  const interactor: MyShowsInteractor = new MyShowsInteractor();
  const response = await interactor.fetchMyHubCarousels({
    myHubFilter: filter,
    orderBy: MY_HUB_ORDER_BY[getState().myHub.selectedSort],
    offset,
    limit: MY_HUB_CAROUSEL_PAGE_SIZE,
    countPerCarousel: CAROUSEL_TILE_PAGE_SIZE
  });
  const carouselList = response as unknown as TMyHubCarouselList;

  const carousels: TMyHubCarouselData[] = carouselList.carousels.map(
    (carousel: TMyHubCarousel): TMyHubCarouselData =>
      mapMyHubCarousel(carousel, imageHostBaseUrl)
  );

  return {
    carousels,
    tilePagination: mapMyHubCarouselsToInitialTilePagination(carousels),
    totalCount: response.totalCount
  };
};

/**
 * Fetches the initial page of My Hub carousel rows (offset 0) — dispatched on
 * mount and whenever the active filter/tab changes.
 */
export const fetchMyHubCarousels = createAsyncThunk<
  TMyHubCarouselsPagePayload,
  void,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/fetchMyHubCarousels",
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const filter: MyHubFilterId = getState().myHub.selectedFilter;
    try {
      Log.i(TAG, `fetchMyHubCarousels() — filter=${filter}`);
      // viewedScreen fires on exit, not here — see logMyHubTabExit().
      const payload: TMyHubCarouselsPagePayload = await fetchMyHubCarouselsPage(
        getState,
        0
      );
      Log.d(
        TAG,
        `fetchMyHubCarousels() SUCCESS — rows=${payload.carousels.length}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "fetchMyHubCarousels() succeeded",
        DiagnosticSeverity.Debug,
        {
          event: "carousels_loaded",
          filter,
          row_count: String(payload.carousels.length)
        }
      );
      return payload;
    } catch (error: unknown) {
      Log.e(TAG, "fetchMyHubCarousels()", error);
      logMyHubApiFailure(
        error,
        ErrorTypes.MY_SHOWS_ERROR_A754,
        "fetchMyHubCarousels"
      );
      dispatch(
        reportMyHubScreenFailure(error, ErrorTypes.MY_SHOWS_ERROR_A754, {
          hasContent: getState().myHub.carouselsPagination.items.length > 0,
          retry: (): void => {
            dispatch(fetchMyHubCarousels());
          }
        })
      );
      return rejectWithValue(
        getMyHubErrorMessage(error, ErrorTypes.MY_SHOWS_ERROR_A754)
      );
    }
  }
);

/**
 * Pages more carousel ROWS into the grid past the initial slice, appending
 * onto `state.carouselsPagination` — the row-level counterpart of
 * {@link loadMoreMyHubTiles}. Offset/limit based rather than cursor based:
 * `totalCount` (from the X-Total-Count response header) is the total row
 * count for the active filter/tab, guarded by {@link canLoadMoreMyHubCarousels}.
 */
export const loadMoreMyHubCarousels = createAsyncThunk<
  TMyHubCarouselsPagePayload,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "myHub/loadMoreMyHubCarousels",
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    try {
      const offset: number = getState().myHub.carouselsPagination.items.length;
      Log.i(
        TAG,
        `loadMoreMyHubCarousels() — offset=${offset}, limit=${MY_HUB_CAROUSEL_PAGE_SIZE}`
      );
      return await fetchMyHubCarouselsPage(getState, offset);
    } catch (error: unknown) {
      Log.e(TAG, "loadMoreMyHubCarousels()", error);
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "loadMoreMyHubCarousels() failed",
        DiagnosticSeverity.Warning,
        {
          event: "load_more_carousels_failed",
          filter: getState().myHub.selectedFilter
        }
      );
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.MY_SHOWS_ERROR_A754,
        "loadMoreMyHubCarousels"
      );
      return rejectWithValue(
        getMyHubErrorMessage(error, ErrorTypes.MY_SHOWS_ERROR_A754)
      );
    }
  },
  {
    condition: (_: void, { getState }): boolean =>
      canLoadMoreMyHubCarousels(getState().myHub.carouselsPagination)
  }
);

/**
 * Pages tiles into one My Hub carousel row past its initial slice, appending
 * onto `state.tilePagination[carouselId]` — the My Hub twin of Home Screen's
 * `loadMoreTiles` (`homescreenSlice.ts`), built from the same
 * `createLoadMoreTilesThunk` factory so the offset/count math, the
 * `resolvedCount`-driven guard and the See More cap all come from the shared
 * `carouselPagination.ts` utility rather than being reimplemented here.
 */
export const loadMoreMyHubTiles = createLoadMoreTilesThunk({
  thunkPrefix: "myHub",
  tag: TAG,
  getTileState: (state: UnityRootState, carouselId: string) =>
    state.myHub.tilePagination[carouselId],
  fetchPage: fetchMyHubCarouselTilePage,
  isSeeMoreNeeded: true
});

/** What {@link fetchMyHubSavedList} resolves to. */
type TMyHubSavedListPayload = {
  items: TMyHubSavedItem[];
  summary: TMyHubSavedListSummary | null;
  carouselId: string | null;
  resolvedCount: number | null;
};

/**
 * Fetches one of the flat-list tabs — Saved or Deleted — and maps it onto the
 * card list those designs render (Figma: My Hub Detail — nodes 115:780 and
 * 71:2130).
 *
 * Dispatched when a flat-list filter becomes active rather than once per mount:
 * the allowance in the summary line changes as the user saves, unsaves and
 * deletes, so a stale list would misreport it.
 *
 * Both tabs are the same `GET /v1/myHub/carousels` call as every other tab, only
 * with a different `myHubFilter`: the service returns exactly ONE carousel — the
 * flat vertical list — plus the `header` the summary line and its bulk button
 * are built from. `mapMyHubSavedList` already handles both halves.
 *
 * The filter is an argument rather than a `getState()` read so the thunk states
 * which tab it fetched, and so a tab switch cannot race a request already in
 * flight into mapping its response as the wrong tab.
 *
 * Wired the same way as {@link fetchMyHubCarousels} — same interactor call,
 * only the `myHubFilter` differs.
 */
export const fetchMyHubSavedList = createAsyncThunk<
  TMyHubSavedListPayload,
  MyHubFilterId,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/fetchMyHubSavedList",
  async (filter: MyHubFilterId, { dispatch, getState, rejectWithValue }) => {
    try {
      Log.i(TAG, `fetchMyHubSavedList() — fetching My Hub list ${filter}`);

      const imageHostBaseUrl: string = await resolveImageHostBaseUrl(
        "fetchMyHubSavedList"
      );

      const interactor: MyShowsInteractor = new MyShowsInteractor();
      const response: TMyHubCarouselList =
        (await interactor.fetchMyHubCarousels({
          myHubFilter: filter,
          orderBy: MY_HUB_ORDER_BY[getState().myHub.selectedSort]
        })) as unknown as TMyHubCarouselList;

      // viewedScreen fires on exit, not here — see logMyHubTabExit().
      const payload = mapMyHubSavedList(response, imageHostBaseUrl, filter);
      Log.d(
        TAG,
        `fetchMyHubSavedList() SUCCESS — count=${payload.items.length}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "fetchMyHubSavedList() succeeded",
        DiagnosticSeverity.Debug,
        {
          event: "saved_list_loaded",
          filter,
          count: String(payload.items.length)
        }
      );
      return payload;
    } catch (error: unknown) {
      Log.e(TAG, "fetchMyHubSavedList()", error);
      logMyHubApiFailure(
        error,
        ErrorTypes.MY_SHOWS_ERROR_A754,
        "fetchMyHubSavedList"
      );
      dispatch(
        reportMyHubScreenFailure(error, ErrorTypes.MY_SHOWS_ERROR_A754, {
          hasContent: getState().myHub.savedList.items.length > 0,
          retry: (): void => {
            dispatch(fetchMyHubSavedList(filter));
          }
        })
      );
      return rejectWithValue(
        getMyHubErrorMessage(error, ErrorTypes.MY_SHOWS_ERROR_A754)
      );
    }
  }
);

/** What {@link loadMoreMyHubSavedList} resolves to. */
type TMyHubSavedListPage = {
  items: TMyHubSavedItem[];
  resolvedCount: number;
};

/**
 * Whether another page of the flat Saved/Deleted list can be requested —
 * the flat-list twin of {@link canLoadMoreMyHubCarousels}.
 */
const canLoadMoreMyHubSavedList = (state: MyHubState): boolean => {
  const { savedList } = state;
  if (savedList.carouselId === null) {
    return false;
  }
  const hasMore: boolean =
    savedList.resolvedCount === null ||
    savedList.items.length < savedList.resolvedCount;
  return hasMore && !savedList.isLoading && !savedList.isLoadingMore;
};

/**
 * Pages more rows into the flat Saved/Deleted list past its initial slice,
 * appending onto `state.savedList` — the flat-list twin of
 * {@link loadMoreMyHubTiles}.
 *
 * The single carousel behind the flat list (see {@link fetchMyHubSavedList})
 * is paged the same way a carousel row's tiles are: `GET
 * /v1/myHub/carousels/{carouselId}` with offset/limit. `myHubFilter` is
 * omitted from the request since `carouselId` already scopes it — only the
 * mapper still needs the active filter, to word the Saved/Deleted rows
 * correctly, so it is read off state rather than threaded through the
 * request.
 */
export const loadMoreMyHubSavedList = createAsyncThunk<
  TMyHubSavedListPage,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "myHub/loadMoreMyHubSavedList",
  async (_arg: void, { dispatch, getState, rejectWithValue }) => {
    const { savedList, selectedFilter } = getState().myHub;
    const carouselId: string = savedList.carouselId as string;
    try {
      Log.i(TAG, `loadMoreMyHubSavedList() — offset=${savedList.items.length}`);

      const imageHostBaseUrl: string = await resolveImageHostBaseUrl(
        "loadMoreMyHubSavedList"
      );

      const interactor: MyShowsInteractor = new MyShowsInteractor();
      const carousel: TMyHubCarousel = (await interactor.fetchMyHubCarousel({
        carouselId,
        offset: savedList.items.length,
        limit: CAROUSEL_TILE_PAGE_SIZE
      })) as unknown as TMyHubCarousel;

      return mapMyHubSavedPage(carousel, imageHostBaseUrl, selectedFilter);
    } catch (error: unknown) {
      Log.e(TAG, "loadMoreMyHubSavedList()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.MY_SHOWS_ERROR_A754,
        "loadMoreMyHubSavedList"
      );
      return rejectWithValue(
        getMyHubErrorMessage(error, ErrorTypes.MY_SHOWS_ERROR_A754)
      );
    }
  },
  {
    condition: (_arg: void, { getState }): boolean =>
      canLoadMoreMyHubSavedList(getState().myHub)
  }
);

/** What {@link fetchMyHubSeriesDetail} resolves to. */
type TMyHubSeriesPayload = {
  items: TMyHubSavedItem[];
  summary: TMyHubSeriesSummary | null;
};

/**
 * Fetches one collection/folder and maps it onto the manage-series screen
 * (Figma: My Hub Detail — node 71:4516, "Managed recordings -series").
 *
 * Keyed by `unifiedCollectionId` rather than by filter: this is a different
 * endpoint from the tabs — one folder per request — and the collection is what
 * the user opened.
 *
 * Wired to `GET /v1/myHub/myHubFolder/unifiedCollection/{unifiedCollectionId}`
 * via `MyShowsInteractor.fetchMyHubFolder`.
 *
 * TODO(UAW): Resolve each item's station call sign from the channel list —
 * `mapMyHubFolderDetail` is called without a `resolveCallSign`, so it defaults
 * to `""` for every row until that lookup exists.
 */
export const fetchMyHubSeriesDetail = createAsyncThunk<
  TMyHubSeriesPayload,
  string,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/fetchMyHubSeriesDetail",
  async (
    unifiedCollectionId: string,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      Log.i(
        TAG,
        `fetchMyHubSeriesDetail() — fetching folder ${unifiedCollectionId}`
      );

      const imageHostBaseUrl: string = await resolveImageHostBaseUrl(
        "fetchMyHubSeriesDetail"
      );

      const interactor: MyShowsInteractor = new MyShowsInteractor();
      // The folder-detail endpoint only distinguishes recorded/scheduled — the
      // Saved and Deleted tabs never open a manage-series screen — so any other
      // selected filter falls back to the recorded variant.
      const folderFilter: MyHubFilterId.RECORDED | MyHubFilterId.SCHEDULED =
        getState().myHub.selectedFilter === MyHubFilterId.SCHEDULED
          ? MyHubFilterId.SCHEDULED
          : MyHubFilterId.RECORDED;
      const response: TMyHubFolderDetail = (await interactor.fetchMyHubFolder({
        unifiedCollectionId,
        myHubFilter: folderFilter,
        orderBy: MY_HUB_ORDER_BY[getState().myHub.selectedSort]
      })) as unknown as TMyHubFolderDetail;

      // Each row's airing line names the channel it aired on ("Today
      // 9:00-10:00pm on NBC"), exactly as the recording highlight does. The
      // wire carries only `stationId` and the mappers are synchronous, so the
      // names are resolved here and handed down as a lookup — deduplicated
      // inside `resolveChannelNames`, since a series folder is usually one
      // channel repeated. A station that cannot be resolved costs its own
      // clause and nothing else.
      const channelNames: Record<string, string> = await resolveChannelNames(
        (response.items || []).map(
          (item: TMyHubItemUnion): string | undefined =>
            (item as TRecordingMyHubItem).stationId
        )
      );

      // Captures the entry screen/time here rather than logging immediately —
      // viewedScreen fires once the drill-in is actually left, with a real
      // time_on_screen — see logManageSeriesExit().
      manageSeriesEntryScreenId = MY_HUB_SCREEN_ID;
      manageSeriesEntryTimestamp = Date.now();
      const payload = mapMyHubFolderDetail(
        response,
        imageHostBaseUrl,
        toCallSignResolver(channelNames),
        folderFilter
      );
      Log.d(
        TAG,
        `fetchMyHubSeriesDetail() SUCCESS — count=${payload.items.length}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "fetchMyHubSeriesDetail() succeeded",
        DiagnosticSeverity.Debug,
        {
          event: "series_detail_loaded",
          unifiedCollectionId,
          count: String(payload.items.length)
        }
      );
      return payload;
    } catch (error: unknown) {
      Log.e(TAG, "fetchMyHubSeriesDetail()", error);
      logMyHubApiFailure(
        error,
        ErrorTypes.MY_HUB_MANAGED_RECORDINGS_ERROR_A781,
        "fetchMyHubSeriesDetail"
      );
      const { series } = getState().myHub;
      dispatch(
        reportMyHubScreenFailure(
          error,
          ErrorTypes.MY_HUB_MANAGED_RECORDINGS_ERROR_A781,
          {
            hasContent:
              series.unifiedCollectionId === unifiedCollectionId &&
              series.items.length > 0,
            retry: (): void => {
              dispatch(fetchMyHubSeriesDetail(unifiedCollectionId));
            }
          }
        )
      );
      return rejectWithValue(
        getMyHubErrorMessage(
          error,
          ErrorTypes.MY_HUB_MANAGED_RECORDINGS_ERROR_A781
        )
      );
    }
  }
);

/**
 * Argument shape shared by {@link cancelSeriesRecordings} and
 * {@link deleteAllSeriesRecordings} — the series to act on, plus its title so
 * the `cancelOnePass`/`deleteRecording` event each fires reports a
 * `collection_title`, not a blank one.
 */
interface TMyHubSeriesMutationParams {
  unifiedCollectionId: string;
  seriesTitle: string;
}

/**
 * Cancels the series' OnePass — stops future episodes from being scheduled.
 * Refreshes on success via {@link refreshMyHubAfterAction} so the header's
 * folder-action row (and any already-scheduled rows) reflect the
 * cancellation immediately.
 */
export const cancelSeriesRecordings = createAsyncThunk<
  void,
  TMyHubSeriesMutationParams,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/cancelSeriesRecordings",
  async (
    { unifiedCollectionId, seriesTitle },
    { dispatch, rejectWithValue }
  ) => {
    try {
      Log.i(
        TAG,
        `cancelSeriesRecordings() — cancelling ucid=${unifiedCollectionId}`
      );
      const interactor: OnePassInteractor = new OnePassInteractor();
      // Fires `cancelOnePass` on success — see OnePassInteractor.cancelSchedule().
      await interactor.cancelSchedule(unifiedCollectionId, undefined, {
        collection_id: unifiedCollectionId,
        collection_title: seriesTitle,
        screen: MY_HUB_SCREEN_NAME
      });
      Log.d(
        TAG,
        `cancelSeriesRecordings() SUCCESS — ucid=${unifiedCollectionId}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "cancelSeriesRecordings() succeeded",
        DiagnosticSeverity.Debug,
        { event: "cancel_series_recordings_succeeded", unifiedCollectionId }
      );
      // The carousel grid behind this folder may now show a stale episode
      // count / scheduled state — flag it for exitMyHubSeriesScreen to
      // refresh once the user actually leaves the folder.
      dispatch(setCarouselRefreshPending(true));
      dispatch(refreshMyHubAfterAction());
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "cancelSeriesRecordings()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.ONEPASS_ACTION_ERROR_R325,
        "cancelSeriesRecordings"
      );
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to cancel series recording"
      );
    }
  }
);

/**
 * Deletes every already-recorded episode of the series in one bulk call.
 *
 * Deliberately does not refresh, unlike {@link cancelSeriesRecordings} above:
 * a fully emptied series has nothing left to show, so `deleteAllRecordingsAction`
 * leaves the folder screen instead, and refetching it here would put a
 * response in flight that lands on the landing screen after the folder has
 * closed. What to do next is the caller's call — see that handler's
 * `exitSurface`, which is why this resolves with the number of recordings it
 * deleted rather than with nothing.
 */
export const deleteAllSeriesRecordings = createAsyncThunk<
  number,
  TMyHubSeriesMutationParams,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/deleteAllSeriesRecordings",
  async (
    { unifiedCollectionId, seriesTitle },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const recordingIds: string[] = getState()
        .myHub.series.items.map(
          (item: TMyHubSavedItem): string | undefined => item.recordingId
        )
        .filter((id): id is string => id !== undefined);

      if (recordingIds.length === 0) {
        Log.i(
          TAG,
          "deleteAllSeriesRecordings() — no recorded episodes to delete"
        );
        return 0;
      }

      Log.i(
        TAG,
        `deleteAllSeriesRecordings() — deleting ${recordingIds.length} recording(s) for ucid=${unifiedCollectionId}`
      );
      const interactor: RecordingsServiceInteractor =
        new RecordingsServiceInteractor();
      // Fires `deleteRecording` on success — see
      // RecordingsServiceInteractor.deleteRecordingsByRecordingIdsV2().
      await interactor.deleteRecordingsByRecordingIdsV2(
        { requestBody: recordingIds },
        undefined,
        {
          collection_id: unifiedCollectionId,
          collection_title: seriesTitle,
          screen: MY_HUB_SCREEN_NAME,
          series_recording: "true"
        }
      );
      Log.d(
        TAG,
        `deleteAllSeriesRecordings() SUCCESS — deleted ${recordingIds.length} recording(s) for ucid=${unifiedCollectionId}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "deleteAllSeriesRecordings() succeeded",
        DiagnosticSeverity.Debug,
        {
          event: "delete_all_series_recordings_succeeded",
          unifiedCollectionId,
          count: String(recordingIds.length)
        }
      );
      // See cancelSeriesRecordings() above for why.
      dispatch(setCarouselRefreshPending(true));
      return recordingIds.length;
    } catch (error: unknown) {
      Log.e(TAG, "deleteAllSeriesRecordings()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "deleteAllSeriesRecordings"
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to delete recordings"
      );
    }
  }
);

/**
 * Permanently deletes every recording on the Deleted tab. Unscoped — no
 * ucid, unlike the series-folder thunks above: it acts on the subscriber's
 * whole Deleted list. Refreshes on success via {@link refreshMyHubAfterAction}
 * so the emptied tab reflects it immediately.
 */
export const permanentlyDeleteAllRecordings = createAsyncThunk<
  void,
  void,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/permanentlyDeleteAllRecordings",
  async (_arg: void, { dispatch, rejectWithValue }) => {
    try {
      Log.i(TAG, "permanentlyDeleteAllRecordings() — deleting Deleted tab");
      const interactor: MyShowsInteractor = new MyShowsInteractor();
      await interactor.deleteMyHubPermanent();
      Log.d(TAG, "permanentlyDeleteAllRecordings() SUCCESS");
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "permanentlyDeleteAllRecordings() succeeded",
        DiagnosticSeverity.Debug,
        { event: "permanently_delete_all_succeeded" }
      );
      AnalyticsLogger.logDeleteRecording({
        is_soft_delete: "false",
        screen: MY_HUB_SCREEN_NAME
      });
      dispatch(refreshMyHubAfterAction());
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "permanentlyDeleteAllRecordings()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "permanentlyDeleteAllRecordings"
      );
      return rejectWithValue(
        getMyHubErrorMessage(error, ErrorTypes.RECORDING_ACTION_ERROR_R323)
      );
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Item-scoped recording actions — one recording, not a whole series or tab.
// The interactor call is all that lives here now: every caller (today just
// `myHubActionHandlers.ts`, via `performMyHubAction`) dispatches
// {@link refreshMyHubAfterAction} itself once the thunk resolves, so the
// same "refetch what's on screen, land on the first item" behaviour applies
// uniformly regardless of which of these ran.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Argument shape shared by {@link deleteMyHubRecording}, {@link stopMyHubRecording}
 * and {@link undeleteMyHubRecording} — the recording to act on, plus whatever
 * analytics context the calling surface has (its screen name, the item's
 * content id/title) so the `deleteRecording`/`cancelRecording`/`modifyRecording`
 * event the interactor fires on success carries a `screen`, not a blank one.
 */
interface TMyHubRecordingMutationParams {
  recordingId: string;
  analyticsFields?: Partial<TAnalyticsFields>;
}

/**
 * Deletes a recording — recoverable, it moves to the Deleted tab. Also flags
 * the carousel grid for a refresh (see {@link exitMyHubSeriesScreen}) when
 * this delete happened on an episode row inside an open manage-series/
 * scheduled-series folder, since that folder's originating carousel row may
 * now show a stale episode.
 */
export const deleteMyHubRecording = createAsyncThunk<
  void,
  TMyHubRecordingMutationParams,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/deleteMyHubRecording",
  async (
    { recordingId, analyticsFields },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      Log.i(TAG, `deleteMyHubRecording() — recordingId=${recordingId}`);
      const interactor: RecordingsServiceInteractor =
        new RecordingsServiceInteractor();
      await interactor.deleteRecordingsByRecordingIdsV2(
        { requestBody: [recordingId] },
        undefined,
        analyticsFields
      );
      Log.d(TAG, `deleteMyHubRecording() SUCCESS — recordingId=${recordingId}`);
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "deleteMyHubRecording() succeeded",
        DiagnosticSeverity.Debug,
        { event: "delete_recording_succeeded", recordingId }
      );
      if (getState().myHub.series.unifiedCollectionId !== "") {
        dispatch(setCarouselRefreshPending(true));
      }
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "deleteMyHubRecording()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "deleteMyHubRecording"
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to delete recording"
      );
    }
  }
);

/**
 * Stops an in-progress recording, keeping what has been recorded so far.
 * Cancels the schedule rather than deleting the item — what is already
 * recorded stays, which is what separates Stop from Delete.
 */
export const stopMyHubRecording = createAsyncThunk<
  void,
  TMyHubRecordingMutationParams,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/stopMyHubRecording",
  async (
    { recordingId, analyticsFields },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      Log.i(TAG, `stopMyHubRecording() — recordingId=${recordingId}`);
      await new RecordingsServiceInteractor().deleteRecordingScheduleV2(
        { recordingId },
        undefined,
        analyticsFields
      );
      Log.d(TAG, `stopMyHubRecording() SUCCESS — recordingId=${recordingId}`);
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "stopMyHubRecording() succeeded",
        DiagnosticSeverity.Debug,
        { event: "stop_recording_succeeded", recordingId }
      );
      // See cancelSeriesRecordings() above for why.
      if (getState().myHub.series.unifiedCollectionId !== "") {
        dispatch(setCarouselRefreshPending(true));
      }
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "stopMyHubRecording()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "stopMyHubRecording"
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to stop recording"
      );
    }
  }
);

/** Restores a deleted recording. */
export const undeleteMyHubRecording = createAsyncThunk<
  void,
  TMyHubRecordingMutationParams,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/undeleteMyHubRecording",
  async ({ recordingId, analyticsFields }, { dispatch, rejectWithValue }) => {
    try {
      Log.i(TAG, `undeleteMyHubRecording() — recordingId=${recordingId}`);
      await new RecordingsServiceInteractor().recoverRecording(
        { recordingId },
        undefined,
        { reason: "undelete", ...analyticsFields }
      );
      Log.d(
        TAG,
        `undeleteMyHubRecording() SUCCESS — recordingId=${recordingId}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "undeleteMyHubRecording() succeeded",
        DiagnosticSeverity.Debug,
        { event: "undelete_recording_succeeded", recordingId }
      );
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "undeleteMyHubRecording()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "undeleteMyHubRecording"
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to restore recording"
      );
    }
  }
);

/**
 * Saves the recording past its expiry.
 *
 * Saved state is its own resource — `PUT /v1/recordings/recordingId/
 * {recordingId}/save`, addressed by the recordingId alone — not the
 * schedule's `keepUntil` option, which is retention policy on a scheduled
 * recording and would mean a read-modify-write of the whole schedule.
 */
export const saveMyHubRecording = createAsyncThunk<
  void,
  TMyHubRecordingMutationParams,
  { state: UnityRootState; rejectValue: string }
>(
  "myHub/saveMyHubRecording",
  async (
    { recordingId, analyticsFields: _analyticsFields },
    { dispatch, rejectWithValue }
  ) => {
    try {
      Log.i(TAG, `saveMyHubRecording() — recordingId=${recordingId}`);
      await new RecordingsServiceInteractor().saveRecording({ recordingId });
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "saveMyHubRecording()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "saveMyHubRecording"
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to save recording"
      );
    }
  }
);

/**
 * Drops the recording's saved status, letting it expire again — the same
 * resource as {@link saveMyHubRecording}, removed rather than created.
 */
export const unsaveMyHubRecording = createAsyncThunk<
  void,
  TMyHubRecordingMutationParams,
  { state: UnityRootState; rejectValue: string }
>(
  "myHub/unsaveMyHubRecording",
  async (
    { recordingId, analyticsFields: _analyticsFields },
    { dispatch, rejectWithValue }
  ) => {
    try {
      Log.i(TAG, `unsaveMyHubRecording() — recordingId=${recordingId}`);
      await new RecordingsServiceInteractor().unsaveRecording({ recordingId });
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "unsaveMyHubRecording()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "unsaveMyHubRecording"
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to unsave recording"
      );
    }
  }
);

/**
 * Extends a recording's expiry window.
 *
 * TODO(UAW): No extend endpoint is wrapped, and the popup renders no button
 * for it either — see the unrenderable list in `recordingHighlightMappers`.
 */
export const extendMyHubRecording = createAsyncThunk<
  void,
  string,
  { state: UnityRootState; rejectValue: string }
>("myHub/extendMyHubRecording", (recordingId: string) => {
  Log.i(TAG, `extendMyHubRecording() — recordingId=${recordingId}`);
  return undefined;
});

/**
 * Removes a deleted recording for good — `DELETE /v1/recordings/recordingId/
 * {recordingId}/permanent`, the single-row counterpart to
 * {@link permanentlyDeleteAllRecordings}.
 *
 * Destructive and irreversible, so the caller confirms before this ever
 * dispatches — see `permanentlyDeleteAction`. The refresh afterwards is
 * `performMyHubAction`'s, like every other item-scoped action above.
 */
export const permanentlyDeleteMyHubRecording = createAsyncThunk<
  void,
  TMyHubRecordingMutationParams,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "myHub/permanentlyDeleteMyHubRecording",
  async ({ recordingId, analyticsFields }, { dispatch, rejectWithValue }) => {
    try {
      Log.i(
        TAG,
        `permanentlyDeleteMyHubRecording() — recordingId=${recordingId}`
      );
      // Fires `deleteRecording` with `is_soft_delete: "false"` on success —
      // see RecordingsServiceInteractor.permanentlyDeleteRecording().
      await new RecordingsServiceInteractor().permanentlyDeleteRecording(
        { recordingId },
        undefined,
        analyticsFields
      );
      Log.d(
        TAG,
        `permanentlyDeleteMyHubRecording() SUCCESS — recordingId=${recordingId}`
      );
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "permanentlyDeleteMyHubRecording() succeeded",
        DiagnosticSeverity.Debug,
        { event: "permanently_delete_recording_succeeded", recordingId }
      );
      return undefined;
    } catch (error: unknown) {
      Log.e(TAG, "permanentlyDeleteMyHubRecording()", error);
      reportMyHubActionFailure(
        dispatch,
        error,
        ErrorTypes.RECORDING_ACTION_ERROR_R323,
        "permanentlyDeleteMyHubRecording"
      );
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to permanently delete recording"
      );
    }
  }
);

// ── Initial state ─────────────────────────────────────────────────────────────

const INITIAL_STATE: MyHubState = {
  filters: {
    items: [],
    isLoading: false,
    error: "",
    requestId: null
  },
  carouselsPagination: {
    items: [],
    totalCount: null,
    isLoading: false,
    isLoadingMore: false,
    error: "",
    requestId: null
  },
  tilePagination: {},
  savedList: {
    items: [],
    summary: null,
    carouselId: null,
    resolvedCount: null,
    isLoading: false,
    isLoadingMore: false,
    error: "",
    requestId: null
  },
  series: {
    items: [],
    summary: null,
    isLoading: false,
    error: "",
    unifiedCollectionId: "",
    requestId: null
  },
  focusedSeriesActionIndex: 0,
  focusedSavedIndex: 0,
  focusedSavedActionIndex: 0,
  selectedFilter: MyHubFilterId.RECORDED,
  selectedSort: MyHubSortId.RECENT,
  recordingQuota: null,
  focusRegion: MyHubFocusRegion.FILTERS,
  isExiting: false,
  focusedFilterIndex: 0,
  focusedSortIndex: 0,
  focusedCarouselIndex: 0,
  focusedItemIndex: 0,
  carouselScrollPositions: [],
  carouselItemCounts: [],
  scrollInstantly: false,
  carouselRefreshPending: false,
  landingSort: MyHubSortId.RECENT
};

// ── Slice ─────────────────────────────────────────────────────────────────────

/**
 * Fulfilled-action shape carrying the requestId the stale-response guards
 * read, plus the thunk's own `arg` for guards that also key off it (e.g.
 * `fetchMyHubSeriesDetail`'s freshness check against `unifiedCollectionId`).
 */
type TGuardedAction<P, A = unknown> = PayloadAction<
  P,
  string,
  { arg: A; requestId: string }
>;

const myHubSlice = createSlice({
  name: "myHub",
  initialState: INITIAL_STATE,
  reducers: {
    // ── Focus setters (mouse / hover path) ──

    /**
     * Applies the filter chip at the given index as the active filter and
     * resets carousel / tile focus to the top-left position.
     *
     * Re-selecting the tab that is already active only moves focus: the
     * content stays as it is. Clearing it would blank the tab for good, since
     * both fetch effects are keyed on `selectedFilter` and so would not re-run.
     * FilterStrip also fires its onSelect from onFocus, which makes landing on
     * the active chip - not just clicking it - a re-select.
     */
    focusMyHubFilterChip(
      state: MyHubState,
      action: PayloadAction<{ filterIndex: number; filterId: MyHubFilterId }>
    ): void {
      state.focusRegion = MyHubFocusRegion.FILTERS;
      state.focusedFilterIndex = action.payload.filterIndex;
      if (state.selectedFilter === action.payload.filterId) {
        return;
      }
      state.selectedFilter = action.payload.filterId;
      clearMyHubContentOnFilterChange(state);
      Log.d(TAG, `focusMyHubFilterChip() — filter=${action.payload.filterId}`);
    },

    /**
     * Applies the sort chip at the given index as the active sort order.
     */
    focusMyHubSortChip(
      state: MyHubState,
      action: PayloadAction<{ sortIndex: number; sortId: MyHubSortId }>
    ): void {
      state.focusRegion = MyHubFocusRegion.SORT;
      state.focusedSortIndex = action.payload.sortIndex;
      if (state.selectedSort === action.payload.sortId) {
        return;
      }
      state.selectedSort = action.payload.sortId;
      Log.d(TAG, `focusMyHubSortChip() — sort=${action.payload.sortId}`);
    },

    /**
     * Moves D-pad / hover focus onto a Saved list row, and optionally onto one
     * of that row's action buttons.
     *
     * Omit `actionIndex` for a row hover — focus then sits on the row's default
     * action. Pass it when the pointer is over a specific button.
     */
    focusMyHubSavedItem(
      state: MyHubState,
      action: PayloadAction<{ itemIndex: number; actionIndex?: number }>
    ): void {
      state.focusRegion = MyHubFocusRegion.SAVED_LIST;
      moveToSavedItem(state, action.payload.itemIndex);
      if (action.payload.actionIndex !== undefined) {
        state.focusedSavedActionIndex = clampIndex(
          action.payload.actionIndex,
          savedActionCount(state, state.focusedSavedIndex)
        );
      }
    },

    /**
     * Moves hover focus onto the flat list's bulk button.
     *
     * Pointer-only in practice — the D-pad reaches the button through
     * `navigateMyHubUp` / `navigateMyHubDown`, which own the vertical order.
     */
    focusMyHubBulkAction(state: MyHubState): void {
      state.focusRegion = MyHubFocusRegion.BULK_ACTION;
    },

    /**
     * Moves D-pad / hover focus onto one of the manage-series header's folder
     * buttons ("Recordings Options", "Cancel Series Recordings", "Delete All
     * Recordings" — Figma node 71:4516).
     */
    focusMyHubSeriesAction(
      state: MyHubState,
      action: PayloadAction<number>
    ): void {
      state.focusRegion = MyHubFocusRegion.SERIES_ACTIONS;
      state.focusedSeriesActionIndex = clampIndex(
        action.payload,
        seriesActionButtonCount(state)
      );
    },

    /**
     * Seeds the series state from one scheduled recording, for the
     * scheduled-single screen (Figma node 407:1709).
     *
     * A single scheduled airing is offer-keyed rather than collection-keyed, so
     * there is no folder to fetch — the tile the cursor was on already carries
     * everything the screen shows. This is therefore the seeded twin of
     * {@link fetchMyHubSeriesDetail}'s `fulfilled` case, and leaves state in
     * the same shape: same rows, same header, and focus on the first row, so
     * the D-pad behaves identically on both Scheduled drill-ins.
     *
     * Dispatched from the screen's own mount rather than before navigating:
     * `leaveMyHubScreen` keeps the visit only while focus is still on the
     * carousel rows, so moving focus here any earlier would reset the very
     * state being seeded.
     */
    seedMyHubScheduledSingle(
      state: MyHubState,
      action: PayloadAction<TCarouselItem>
    ): void {
      const seeded: {
        items: TMyHubSavedItem[];
        summary: TMyHubSeriesSummary;
      } = mapScheduledSingleRecording(action.payload);
      state.series.items = seeded.items;
      state.series.summary = seeded.summary;
      state.series.isLoading = false;
      state.series.error = "";
      state.focusRegion = MyHubFocusRegion.SAVED_LIST;
      state.focusedSeriesActionIndex = 0;
      moveToSeriesItem(state, 0);
      // Always the Scheduled tab — see the class doc on this screen.
      // viewedScreen fires once this drill-in is left — see
      // logManageSeriesExit().
      manageSeriesEntryScreenId = MY_HUB_SCREEN_ID;
      manageSeriesEntryTimestamp = Date.now();
    },

    /**
     * Moves D-pad / hover focus onto a manage-series row, and optionally onto
     * one of that row's action buttons.
     *
     * The series twin of {@link focusMyHubSavedItem}: same contract, bounded by
     * the folder's rows rather than the tab's.
     */
    focusMyHubSeriesItem(
      state: MyHubState,
      action: PayloadAction<{ itemIndex: number; actionIndex?: number }>
    ): void {
      state.focusRegion = MyHubFocusRegion.SAVED_LIST;
      moveToSeriesItem(state, action.payload.itemIndex);
      if (action.payload.actionIndex !== undefined) {
        state.focusedSavedActionIndex = clampIndex(
          action.payload.actionIndex,
          seriesActionCount(state, state.focusedSavedIndex)
        );
      }
    },

    /**
     * Moves D-pad / hover focus to a specific tile in a specific carousel row.
     */
    focusMyHubCarouselTile(
      state: MyHubState,
      action: PayloadAction<{ carouselIndex: number; itemIndex: number }>
    ): void {
      state.focusRegion = MyHubFocusRegion.CAROUSEL_ITEMS;
      state.focusedCarouselIndex = action.payload.carouselIndex;
      state.focusedItemIndex = action.payload.itemIndex;
    },

    // ── D-pad commit actions (OK / Enter on focused chip) ──

    /**
     * Commits the currently highlighted filter chip as the selected filter.
     * Resets carousel and tile focus to the top-left position.
     *
     * Pressing OK on the tab that is already active does nothing - see
     * {@link focusMyHubFilterChip} for why re-selecting must not clear.
     */
    applyMyHubFilter(state: MyHubState): void {
      const filter: TMyHubFilterMeta | undefined =
        state.filters.items[state.focusedFilterIndex];
      if (filter !== undefined && filter.id !== state.selectedFilter) {
        state.selectedFilter = filter.id;
        clearMyHubContentOnFilterChange(state);
        Log.d(TAG, `applyMyHubFilter() — filter=${filter.id}`);
      }
    },

    /**
     * Commits the currently highlighted sort segment as the active sort
     * order — the same shape as {@link applyMyHubFilter}, not a blind toggle
     * to "the other" option. LEFT/RIGHT (see navigateMyHubLeft/Right) already
     * move `focusedSortIndex` between segments like any other row of
     * buttons; this only fires on OK.
     */
    applyMyHubSort(state: MyHubState): void {
      const sort: TMyHubSortMeta | undefined =
        MY_HUB_SORT_OPTIONS[state.focusedSortIndex];
      if (sort !== undefined && sort.id !== state.selectedSort) {
        state.selectedSort = sort.id;
        Log.d(TAG, `applyMyHubSort() — sort=${sort.id}`);
      }
    },

    /**
     * Stores the recording-quota snapshot rendered by the header indicator.
     * Pass `null` to hide the indicator (quota unknown / unavailable).
     *
     * The quota is filter-independent, so it is deliberately left untouched by
     * the filter and sort reducers — the header keeps showing the same value
     * across every My Hub tab.
     *
     * Set by the fetchMyHubRecordingQuota thunk; this reducer exists for tests
     * and for any future push-based update.
     */
    setMyHubRecordingQuota(
      state: MyHubState,
      action: PayloadAction<TMyHubRecordingQuota | null>
    ): void {
      state.recordingQuota = action.payload;
    },

    /**
     * Resets D-pad/pointer focus and scroll-position bookkeeping to the
     * screen's mount defaults.
     *
     * Dispatch from MyHubScreen's unmount cleanup: the Redux store outlives
     * the screen, so without this the cursor position, scroll offsets and
     * carousel-bounds cache from this visit would still be sitting in state
     * the next time My Hub mounts — stale enough to point navigation at rows
     * or tiles the fresh fetch may not even have.
     *
     * Deliberately leaves `selectedFilter` / `selectedSort` and the fetched
     * content (`filters` / `carouselsPagination` / `savedList` / `series`)
     * untouched — {@link resetMyHubContent} is what clears those, dispatched
     * alongside this one from MyHubScreen's unmount cleanup.
     */
    resetMyHubFocus(state: MyHubState): void {
      state.focusRegion = MyHubFocusRegion.FILTERS;
      state.focusedFilterIndex = 0;
      state.focusedSortIndex = 0;
      state.focusedCarouselIndex = 0;
      state.focusedItemIndex = 0;
      state.carouselScrollPositions = [];
      state.carouselItemCounts = [];
      state.focusedSavedIndex = 0;
      state.focusedSavedActionIndex = 0;
      state.focusedSeriesActionIndex = 0;
    },

    /**
     * Clears every previously-fetched My Hub surface — the filter/tab strip,
     * carousels, tile pagination, and both flat lists (Saved/Deleted, manage
     * series) — back to their mount defaults, and returns focus to the filter
     * strip.
     *
     * Dispatch from MyHubScreen's unmount cleanup: the Redux store outlives
     * the screen, so without this a second visit within the same session
     * would flash the PREVIOUS visit's filter chips and carousel rows on
     * screen — and leave them navigable — until the new responses overwrite
     * them, since nothing else clears this content between visits. It also
     * fires a duplicate carousels fetch off those stale chips, which is why
     * this runs on the way out rather than on mount: a mount reset lands in
     * the same effect flush the carousels effect has already read.
     */
    resetMyHubContent(state: MyHubState): void {
      // Nulling the requestIds is what makes this reset stick: the fetches
      // are not cancelled with the screen, so one still in flight would
      // otherwise land later and refill the surface just emptied here.
      state.filters.items = [];
      state.filters.isLoading = false;
      state.filters.error = "";
      state.filters.requestId = null;
      state.carouselsPagination.items = [];
      state.carouselsPagination.totalCount = null;
      state.carouselsPagination.isLoading = false;
      state.carouselsPagination.isLoadingMore = false;
      state.carouselsPagination.error = "";
      state.carouselsPagination.requestId = null;
      state.tilePagination = {};
      state.savedList.items = [];
      state.savedList.summary = null;
      state.savedList.carouselId = null;
      state.savedList.resolvedCount = null;
      state.savedList.isLoading = false;
      state.savedList.isLoadingMore = false;
      state.savedList.error = "";
      state.savedList.requestId = null;
      state.series.items = [];
      state.series.summary = null;
      state.series.isLoading = false;
      state.series.error = "";
      state.series.unifiedCollectionId = "";
      state.focusRegion = MyHubFocusRegion.FILTERS;
      state.focusedFilterIndex = 0;
      state.focusedCarouselIndex = 0;
      state.focusedItemIndex = 0;
      state.carouselItemCounts = [];
      state.focusedSavedIndex = 0;
      state.focusedSavedActionIndex = 0;
    },

    /** BACK sets this, which is what makes {@link leaveMyHubScreen} reset. */
    setMyHubExiting(state: MyHubState, action: PayloadAction<boolean>): void {
      state.isExiting = action.payload;
    },

    /**
     * Marks (or clears) whether the next scroll-to-focus should snap
     * instantly rather than animate. See {@link MyHubState.scrollInstantly}.
     */
    setMyHubScrollInstantly(
      state: MyHubState,
      action: PayloadAction<boolean>
    ): void {
      state.scrollInstantly = action.payload;
    },

    /**
     * Marks (or clears) whether the carousel grid needs a refetch once the
     * open folder screen exits for real. See
     * {@link MyHubState.carouselRefreshPending}.
     */
    setCarouselRefreshPending(
      state: MyHubState,
      action: PayloadAction<boolean>
    ): void {
      state.carouselRefreshPending = action.payload;
    },

    /** Puts the sort back to its default, Recent. */
    resetMyHubSort(state: MyHubState): void {
      state.selectedSort = MyHubSortId.RECENT;
    },

    /**
     * Manage-series screen unmount: keeps the visit when the user drilled into
     * an episode row (Play/Details), otherwise drops the folder and hands the
     * cursor back to the carousel rows. `isExiting` (set by BACK, same flag the
     * landing screen uses) always wins, since BACK can be pressed with a row
     * focused too.
     */
    leaveMyHubSeriesScreen(state: MyHubState): void {
      if (
        state.focusRegion === MyHubFocusRegion.SAVED_LIST &&
        !state.isExiting
      ) {
        // A drill-in from a row (Play/Details) — the visit continues once the
        // user returns, so it hasn't ended yet; no viewedScreen here.
        return;
      }
      logManageSeriesExit();
      state.series.items = [];
      state.series.summary = null;
      state.series.isLoading = false;
      state.series.error = "";
      state.series.unifiedCollectionId = "";
      // Nulling the requestId is what makes this stick, exactly as in
      // `resetMyHubContent`: the folder fetch is not cancelled with the
      // screen, so one still in flight would otherwise land afterwards and
      // reopen the folder that was just closed.
      state.series.requestId = null;
      state.focusRegion = MyHubFocusRegion.CAROUSEL_ITEMS;
      // Back to the landing screen's own sort — see MyHubState.landingSort.
      state.selectedSort = state.landingSort;
    },

    /** Non-sticky exit: every fetched surface and cursor back to mount defaults. */
    resetMyHubState(): MyHubState {
      return { ...INITIAL_STATE };
    },

    /** Sticky exit: keeps the carousel rows, their tiles and the cursor for the next visit. */
    persistMyHubState(state: MyHubState): void {
      // Restored when a folder opened from here is left — see
      // MyHubState.landingSort.
      state.landingSort = state.selectedSort;
      // Only the transient bits go — a spinner or error banner must not come
      // back with the restored rows. The flat list needs no equivalent
      // clearing here: its container refetches unconditionally on mount, and
      // that fetch's own `pending` case already clears its loading/error state
      // before the restored cursor is ever shown.
      state.carouselsPagination.isLoading = false;
      state.carouselsPagination.isLoadingMore = false;
      state.carouselsPagination.error = "";
      // Nulling the requestId drops a carousels fetch still in flight, which
      // would otherwise land on the restored rows after the screen is gone.
      state.carouselsPagination.requestId = null;
      Object.values(state.tilePagination).forEach(
        (tileState: TTilePaginationState): void => {
          tileState.isLoading = false;
          tileState.error = "";
        }
      );
    },

    // ── D-pad navigation actions ──

    /**
     * Syncs the item count per visible carousel row into state so that
     * navigation reducers can compute bounds without receiving payload from
     * the view layer. Also initializes carouselScrollPositions array to track
     * scroll position for each carousel independently.
     *
     * Dispatch this from a `useEffect` in the view whenever the visible
     * carousel list changes.
     *
     * TODO(UAW): Remove once carousel data is held directly in this slice.
     */
    setCarouselItemCounts(
      state: MyHubState,
      action: PayloadAction<number[]>
    ): void {
      state.carouselItemCounts = action.payload;
      // Initialize scroll positions array with same length, preserving existing positions
      const newScrollPositions: number[] = [];
      for (let i = 0; i < action.payload.length; i++) {
        newScrollPositions.push(
          state.carouselScrollPositions[i] !== undefined
            ? state.carouselScrollPositions[i]
            : 0
        );
      }
      state.carouselScrollPositions = newScrollPositions;
    },

    /**
     * Moves focus left within the active region.
     * Bounds are read from `state.carouselItemCounts`.
     */
    navigateMyHubLeft(state: MyHubState): void {
      if (state.focusRegion === MyHubFocusRegion.FILTERS) {
        state.focusedFilterIndex = clampIndex(
          state.focusedFilterIndex - 1,
          state.filters.items.length
        );
        return;
      }
      // Sort By is a row of buttons like Filters, not a two-way switch: LEFT
      // steps onto the previous segment, and only leaving the leftmost one
      // crosses back into Filters.
      if (state.focusRegion === MyHubFocusRegion.SORT) {
        if (state.focusedSortIndex === 0) {
          state.focusRegion = MyHubFocusRegion.FILTERS;
          state.focusedFilterIndex = Math.max(
            0,
            state.filters.items.length - 1
          );
          return;
        }
        state.focusedSortIndex = clampIndex(
          state.focusedSortIndex - 1,
          MY_HUB_SORT_OPTIONS.length
        );
        return;
      }
      // The bulk button is the only control on its line — Figma node 71:2130
      // puts nothing beside it — so the horizontal axis has nowhere to go.
      if (state.focusRegion === MyHubFocusRegion.BULK_ACTION) {
        return;
      }
      // In the flat list the horizontal axis is the focused row's action row,
      // not a rail of tiles.
      if (state.focusRegion === MyHubFocusRegion.SAVED_LIST) {
        state.focusedSavedActionIndex = clampIndex(
          state.focusedSavedActionIndex - 1,
          savedActionCount(state, state.focusedSavedIndex)
        );
        return;
      }
      const next = moveCarouselTileLeft(state.focusedItemIndex);
      if (next !== null) {
        state.focusedItemIndex = next;
      }
    },

    /**
     * Moves focus right within the active region.
     * Bounds are read from `state.carouselItemCounts`.
     */
    navigateMyHubRight(state: MyHubState): void {
      if (state.focusRegion === MyHubFocusRegion.FILTERS) {
        if (state.focusedFilterIndex === state.filters.items.length - 1) {
          focusSortControl(state);
        } else {
          state.focusedFilterIndex = clampIndex(
            state.focusedFilterIndex + 1,
            state.filters.items.length
          );
        }
        return;
      }
      // Sort By's rightmost segment has nothing beside it (Figma puts nothing
      // after the track), so RIGHT there is a no-op — same shape as Filters'
      // LEFT-at-index-0 having nowhere to go.
      if (state.focusRegion === MyHubFocusRegion.SORT) {
        state.focusedSortIndex = clampIndex(
          state.focusedSortIndex + 1,
          MY_HUB_SORT_OPTIONS.length
        );
        return;
      }
      if (state.focusRegion === MyHubFocusRegion.BULK_ACTION) {
        return;
      }
      if (state.focusRegion === MyHubFocusRegion.SAVED_LIST) {
        state.focusedSavedActionIndex = clampIndex(
          state.focusedSavedActionIndex + 1,
          savedActionCount(state, state.focusedSavedIndex)
        );
        return;
      }
      const rawCount: number | undefined =
        state.carouselItemCounts[state.focusedCarouselIndex];
      const itemCount: number = rawCount !== undefined ? rawCount : 0;
      const next = moveCarouselTileRight(state.focusedItemIndex, itemCount);
      if (next !== null) {
        state.focusedItemIndex = next;
      }
    },

    /**
     * Moves focus upward across regions or carousel rows.
     * Bounds are read from `state.carouselItemCounts`.
     */
    navigateMyHubUp(state: MyHubState): void {
      if (state.focusRegion === MyHubFocusRegion.BULK_ACTION) {
        returnFocusToFilters(state);
        return;
      }
      if (state.focusRegion === MyHubFocusRegion.SAVED_LIST) {
        if (state.focusedSavedIndex === 0) {
          // Back out through the bulk button when the tab has one — it is a
          // stop on the way down, so it has to be one on the way up too.
          if (hasBulkAction(state)) {
            state.focusRegion = MyHubFocusRegion.BULK_ACTION;
            return;
          }
          returnFocusToFilters(state);
          return;
        }
        moveToSavedItem(state, state.focusedSavedIndex - 1);
        return;
      }
      if (state.focusRegion === MyHubFocusRegion.CAROUSEL_ITEMS) {
        if (state.focusedCarouselIndex === 0) {
          returnFocusToFilters(state);
          return;
        }
        const upResult = moveCarouselRowUp(
          state.focusedCarouselIndex,
          state.focusedItemIndex,
          [...state.carouselScrollPositions],
          state.carouselItemCounts
        );
        if (upResult !== null) {
          state.focusedCarouselIndex = upResult.carouselIndex;
          state.focusedItemIndex = upResult.itemIndex;
          state.carouselScrollPositions = upResult.savedScrollPositions;
        }
        return;
      }
      if (state.focusRegion === MyHubFocusRegion.SORT) {
        // Sort By and Filters share the same row — UP is a no-op.
        return;
      }
    },

    /**
     * Moves focus downward across regions or carousel rows.
     * Bounds are read from `state.carouselItemCounts`.
     */
    navigateMyHubDown(state: MyHubState): void {
      if (state.focusRegion === MyHubFocusRegion.SAVED_LIST) {
        moveToSavedItem(state, state.focusedSavedIndex + 1);
        return;
      }

      if (state.focusRegion === MyHubFocusRegion.BULK_ACTION) {
        if (state.savedList.items.length > 0) {
          state.focusRegion = MyHubFocusRegion.SAVED_LIST;
          moveToSavedItem(state, 0);
        }
        return;
      }

      const isTopBar: boolean =
        state.focusRegion === MyHubFocusRegion.FILTERS ||
        state.focusRegion === MyHubFocusRegion.SORT;

      if (isTopBar) {
        // The Saved and Deleted tabs replace the carousel rows with a flat card
        // list, so DOWN out of the top bar enters whichever content surface is
        // on screen.
        if (isMyHubFlatListFilter(state.selectedFilter, state.filters.items)) {
          moveIntoFlatList(state);
          return;
        }
        moveToFirstCarousel(state, state.carouselItemCounts);
        return;
      }

      if (state.focusRegion === MyHubFocusRegion.CAROUSEL_ITEMS) {
        const downResult = moveCarouselRowDown(
          state.focusedCarouselIndex,
          state.focusedItemIndex,
          [...state.carouselScrollPositions],
          state.carouselItemCounts
        );
        if (downResult !== null) {
          state.focusedCarouselIndex = downResult.carouselIndex;
          state.focusedItemIndex = downResult.itemIndex;
          state.carouselScrollPositions = downResult.savedScrollPositions;
        }
      }
    },

    // ── Manage-series D-pad navigation ──
    // Separate reducers rather than extra branches in the four above: the two
    // screens share this slice but not their layouts, and the landing screen's
    // vertical order (tab strip → bulk button → rows → carousels) does not
    // describe this one at all. Keeping them apart means neither screen's focus
    // order can be changed by accident while editing the other's.

    /**
     * Moves focus left on the manage-series screen.
     *
     * Within the folder-button row LEFT walks to the previous button; within
     * the sort control it walks to the previous segment, and only leaving the
     * leftmost one steps back onto the last folder button, since the two sit
     * on the same line (Figma node 71:4516). Within a row it walks the row's
     * action buttons.
     */
    navigateMyHubSeriesLeft(state: MyHubState): void {
      if (state.focusRegion === MyHubFocusRegion.SERIES_ACTIONS) {
        state.focusedSeriesActionIndex = clampIndex(
          state.focusedSeriesActionIndex - 1,
          seriesActionButtonCount(state)
        );
        return;
      }
      // Sort By is a row of buttons here exactly as it is on the landing
      // screen (see navigateMyHubLeft): stepping onto the control has to be
      // able to reach its other segment, or OK can only ever re-commit the
      // order already in force and the control looks dead to a remote.
      if (state.focusRegion === MyHubFocusRegion.SORT) {
        if (state.focusedSortIndex > 0) {
          state.focusedSortIndex = clampIndex(
            state.focusedSortIndex - 1,
            MY_HUB_SORT_OPTIONS.length
          );
          return;
        }
        const buttonCount: number = seriesActionButtonCount(state);
        if (buttonCount > 0) {
          state.focusRegion = MyHubFocusRegion.SERIES_ACTIONS;
          state.focusedSeriesActionIndex = buttonCount - 1;
        }
        return;
      }
      if (state.focusRegion === MyHubFocusRegion.SAVED_LIST) {
        state.focusedSavedActionIndex = clampIndex(
          state.focusedSavedActionIndex - 1,
          seriesActionCount(state, state.focusedSavedIndex)
        );
      }
    },

    /**
     * Moves focus right on the manage-series screen.
     *
     * RIGHT off the end of the folder-button row crosses to the sort control,
     * the only other control on that line, and then walks its segments.
     */
    navigateMyHubSeriesRight(state: MyHubState): void {
      if (state.focusRegion === MyHubFocusRegion.SERIES_ACTIONS) {
        const buttonCount: number = seriesActionButtonCount(state);
        if (state.focusedSeriesActionIndex >= buttonCount - 1) {
          focusSortControl(state);
          return;
        }
        state.focusedSeriesActionIndex = clampIndex(
          state.focusedSeriesActionIndex + 1,
          buttonCount
        );
        return;
      }
      // The rightmost segment has nothing beside it, so RIGHT clamps there —
      // the mirror of the landing screen's own Sort By handling.
      if (state.focusRegion === MyHubFocusRegion.SORT) {
        state.focusedSortIndex = clampIndex(
          state.focusedSortIndex + 1,
          MY_HUB_SORT_OPTIONS.length
        );
        return;
      }
      if (state.focusRegion === MyHubFocusRegion.SAVED_LIST) {
        state.focusedSavedActionIndex = clampIndex(
          state.focusedSavedActionIndex + 1,
          seriesActionCount(state, state.focusedSavedIndex)
        );
      }
    },

    /**
     * Moves focus up on the manage-series screen: out of the rows and back onto
     * the folder-button row, which is the screen's only chrome.
     */
    navigateMyHubSeriesUp(state: MyHubState): void {
      if (state.focusRegion !== MyHubFocusRegion.SAVED_LIST) {
        // The folder buttons and the sort control share one line, so there is
        // nothing above either of them.
        return;
      }
      if (state.focusedSavedIndex === 0) {
        if (seriesActionButtonCount(state) > 0) {
          state.focusRegion = MyHubFocusRegion.SERIES_ACTIONS;
        }
        return;
      }
      moveToSeriesItem(state, state.focusedSavedIndex - 1);
    },

    /**
     * Moves focus down on the manage-series screen: out of the chrome and into
     * the rows, then row by row.
     */
    navigateMyHubSeriesDown(state: MyHubState): void {
      if (state.focusRegion === MyHubFocusRegion.SAVED_LIST) {
        moveToSeriesItem(state, state.focusedSavedIndex + 1);
        return;
      }
      const isChrome: boolean =
        state.focusRegion === MyHubFocusRegion.SERIES_ACTIONS ||
        state.focusRegion === MyHubFocusRegion.SORT;
      if (isChrome) {
        moveIntoSeriesList(state);
      }
    }
  },
  extraReducers: (builder): void => {
    builder
      // Stale-response guard, as in `SearchState.currentRequestId`: only the
      // newest request may write its surface. `resetMyHubContent` nulls these,
      // so a fetch left in flight by a previous My Hub visit is dropped rather
      // than applied over this one's content.
      .addCase(fetchMyHubFilters.pending, (state: MyHubState, action): void => {
        state.filters.requestId = action.meta.requestId;
        state.filters.isLoading = true;
        state.filters.error = "";
      })
      .addCase(
        fetchMyHubFilters.fulfilled,
        (
          state: MyHubState,
          action: TGuardedAction<TMyHubFilterMeta[]>
        ): void => {
          if (action.meta.requestId !== state.filters.requestId) {
            return;
          }
          state.filters.items = action.payload;
          state.filters.isLoading = false;
          // Select whichever filter the service put first — the carousels
          // fetch reads state.selectedFilter, so this is what decides which
          // tab's content it requests once this list has arrived.
          const firstFilter: TMyHubFilterMeta | undefined =
            state.filters.items[0];
          if (firstFilter !== undefined) {
            state.selectedFilter = firstFilter.id;
          }
          state.focusedFilterIndex = 0;
        }
      )
      .addCase(
        fetchMyHubFilters.rejected,
        (state: MyHubState, action): void => {
          if (action.meta.requestId !== state.filters.requestId) {
            return;
          }
          state.filters.isLoading = false;
          state.filters.error =
            action.payload || Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
        }
      )
      .addCase(
        fetchMyHubCarousels.pending,
        (state: MyHubState, action): void => {
          state.carouselsPagination.requestId = action.meta.requestId;
          state.carouselsPagination.isLoading = true;
          state.carouselsPagination.isLoadingMore = false;
          state.carouselsPagination.error = "";
        }
      )
      .addCase(
        fetchMyHubCarousels.fulfilled,
        (
          state: MyHubState,
          action: TGuardedAction<TMyHubCarouselsPagePayload>
        ): void => {
          if (action.meta.requestId !== state.carouselsPagination.requestId) {
            return;
          }
          state.carouselsPagination.items = action.payload.carousels;
          state.carouselsPagination.totalCount = action.payload.totalCount;
          state.carouselsPagination.isLoading = false;
          state.tilePagination = action.payload.tilePagination;
        }
      )
      .addCase(
        fetchMyHubCarousels.rejected,
        (state: MyHubState, action): void => {
          if (action.meta.requestId !== state.carouselsPagination.requestId) {
            return;
          }
          state.carouselsPagination.isLoading = false;
          state.carouselsPagination.error =
            action.payload || Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
        }
      )
      // Load more carousel ROWS (append) — the row-level counterpart of the
      // loadMoreMyHubTiles cases below.
      // Shares `carouselsPagination.requestId` with the initial fetch: both
      // write the one surface, and the two cannot race for it because
      // `canLoadMoreMyHubCarousels` needs the totalCount only that fetch sets.
      .addCase(
        loadMoreMyHubCarousels.pending,
        (state: MyHubState, action): void => {
          state.carouselsPagination.requestId = action.meta.requestId;
          state.carouselsPagination.isLoading = true;
          state.carouselsPagination.isLoadingMore = true;
          state.carouselsPagination.error = "";
        }
      )
      .addCase(
        loadMoreMyHubCarousels.fulfilled,
        (
          state: MyHubState,
          action: TGuardedAction<TMyHubCarouselsPagePayload>
        ): void => {
          if (action.meta.requestId !== state.carouselsPagination.requestId) {
            return;
          }
          state.carouselsPagination.items =
            state.carouselsPagination.items.concat(action.payload.carousels);
          state.carouselsPagination.totalCount = action.payload.totalCount;
          state.carouselsPagination.isLoading = false;
          state.carouselsPagination.isLoadingMore = false;
          // Merge new tile pagination entries — don't overwrite rows already
          // tracked from an earlier page.
          Object.assign(
            state.tilePagination,
            Object.fromEntries(
              Object.entries(action.payload.tilePagination).filter(
                ([key]) => state.tilePagination[key] === undefined
              )
            )
          );
        }
      )
      .addCase(
        loadMoreMyHubCarousels.rejected,
        (state: MyHubState, action): void => {
          if (action.meta.requestId !== state.carouselsPagination.requestId) {
            return;
          }
          state.carouselsPagination.isLoading = false;
          state.carouselsPagination.isLoadingMore = false;
          state.carouselsPagination.error =
            action.payload || Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
        }
      )
      // Load more tiles for a specific My Hub row (append) — same
      // pending/fulfilled/rejected shape as Home Screen's loadMoreTiles cases
      // (homescreenSlice.ts), driven by the shared carouselThunkFactory.
      .addCase(
        loadMoreMyHubTiles.pending,
        (state: MyHubState, action): void => {
          const tileState = state.tilePagination[action.meta.arg.carouselId];
          if (tileState !== undefined) {
            tileState.isLoading = true;
            tileState.error = "";
          }
        }
      )
      .addCase(
        loadMoreMyHubTiles.fulfilled,
        (
          state: MyHubState,
          action: PayloadAction<TLoadMoreTilesPayload>
        ): void => {
          const tileState = state.tilePagination[action.payload.carouselId];
          if (tileState !== undefined) {
            applyOffsetPage(tileState, action.payload.page);
          }
        }
      )
      .addCase(
        loadMoreMyHubTiles.rejected,
        (state: MyHubState, action): void => {
          const tileState = state.tilePagination[action.meta.arg.carouselId];
          if (tileState !== undefined) {
            tileState.isLoading = false;
            tileState.error =
              action.payload ||
              Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
          }
        }
      )
      .addCase(
        fetchMyHubSavedList.pending,
        (state: MyHubState, action): void => {
          state.savedList.requestId = action.meta.requestId;
          state.savedList.isLoading = true;
          state.savedList.error = "";
        }
      )
      .addCase(
        fetchMyHubSavedList.fulfilled,
        (
          state: MyHubState,
          action: TGuardedAction<TMyHubSavedListPayload>
        ): void => {
          if (action.meta.requestId !== state.savedList.requestId) {
            return;
          }
          state.savedList.items = action.payload.items;
          state.savedList.summary = action.payload.summary;
          state.savedList.carouselId = action.payload.carouselId;
          state.savedList.resolvedCount = action.payload.resolvedCount;
          state.savedList.isLoading = false;
          // The list just changed underneath the cursor — re-clamp rather than
          // leaving focus past the end of a shorter list. The current action
          // column is passed through rather than left to the default, so a
          // refetch of the same visit (e.g. the sticky return from
          // Play/Details) does not visibly reset which button was focused.
          moveToSavedItem(
            state,
            state.focusedSavedIndex,
            state.focusedSavedActionIndex
          );
          // Re-clamping is not enough when the list came back empty — index 0
          // of nothing is still nothing to focus. See leaveEmptyFlatList.
          leaveEmptyFlatList(state);
        }
      )
      .addCase(
        fetchMyHubSavedList.rejected,
        (state: MyHubState, action): void => {
          if (action.meta.requestId !== state.savedList.requestId) {
            return;
          }
          state.savedList.isLoading = false;
          state.savedList.error =
            action.payload || Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
        }
      )
      .addCase(
        fetchMyHubSeriesDetail.pending,
        (state: MyHubState, action): void => {
          state.series.requestId = action.meta.requestId;
          state.series.isLoading = true;
          state.series.error = "";
        }
      )
      // A page claims the surface's requestId exactly as a full fetch does:
      // both write `savedList`, so the newest of them has to win. A page cannot
      // start while a fetch is in flight (see canLoadMoreMyHubSavedList), so
      // sharing the field only ever means a later fetch supersedes an earlier
      // page — which is the right outcome.
      .addCase(
        loadMoreMyHubSavedList.pending,
        (state: MyHubState, action): void => {
          state.savedList.requestId = action.meta.requestId;
          state.savedList.isLoadingMore = true;
          state.savedList.error = "";
        }
      )
      .addCase(
        loadMoreMyHubSavedList.fulfilled,
        (
          state: MyHubState,
          action: TGuardedAction<TMyHubSavedListPage>
        ): void => {
          // Without this a page requested on the Saved tab lands after the user
          // has switched to Deleted and concatenates Saved rows onto the other
          // tab's list — the fetch is not cancelled with the tab, and
          // `clearMyHubContentOnFilterChange` only empties what is already
          // there. The carouselId check covers the same race before the new
          // tab's fetch has even been dispatched: the switch nulls it, and a
          // page with no list to belong to has nowhere to go.
          if (
            action.meta.requestId !== state.savedList.requestId ||
            state.savedList.carouselId === null
          ) {
            return;
          }
          state.savedList.items = state.savedList.items.concat(
            action.payload.items
          );
          state.savedList.resolvedCount = action.payload.resolvedCount;
          state.savedList.isLoadingMore = false;
        }
      )
      .addCase(
        loadMoreMyHubSavedList.rejected,
        (state: MyHubState, action): void => {
          if (action.meta.requestId !== state.savedList.requestId) {
            return;
          }
          state.savedList.isLoadingMore = false;
          state.savedList.error =
            action.payload || "Failed to load more My Hub recordings";
        }
      )
      .addCase(
        fetchMyHubSeriesDetail.fulfilled,
        (
          state: MyHubState,
          action: TGuardedAction<TMyHubSeriesPayload, string>
        ): void => {
          if (action.meta.requestId !== state.series.requestId) {
            return;
          }
          // A different collection is a fresh entry: focus goes to the first
          // row with the row cursor at the top. The same collection
          // resolving again (sort change, foreground refresh, sticky return
          // from Play/Details) is this visit settling back in, so focus is
          // re-clamped in place instead of reset.
          const isFreshCollection: boolean =
            state.series.unifiedCollectionId !== action.meta.arg;

          state.series.items = action.payload.items;
          state.series.summary = action.payload.summary;
          state.series.isLoading = false;
          state.series.unifiedCollectionId = action.meta.arg;

          if (isFreshCollection) {
            state.focusRegion = MyHubFocusRegion.SAVED_LIST;
            state.focusedSeriesActionIndex = 0;
            moveToSeriesItem(state, 0);
            // A folder with no rows opens on its folder buttons instead.
            leaveEmptySeriesList(state);
            return;
          }
          moveToSeriesItem(
            state,
            state.focusedSavedIndex,
            state.focusedSavedActionIndex
          );
          // The folder can empty out under the cursor — see leaveEmptySeriesList.
          leaveEmptySeriesList(state);
        }
      )
      .addCase(
        fetchMyHubSeriesDetail.rejected,
        (state: MyHubState, action): void => {
          if (action.meta.requestId !== state.series.requestId) {
            return;
          }
          state.series.isLoading = false;
          state.series.error =
            action.payload || Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
        }
      )
      .addCase(
        fetchMyHubRecordingQuota.fulfilled,
        (
          state: MyHubState,
          action: PayloadAction<TMyHubRecordingQuota>
        ): void => {
          state.recordingQuota = action.payload;
        }
      )
      .addCase(fetchMyHubRecordingQuota.rejected, (state: MyHubState): void => {
        // Hide the indicator rather than leaving a stale figure on screen —
        // a wrong quota is worse than none.
        state.recordingQuota = null;
      });
  }
});

// ── Exported actions ──────────────────────────────────────────────────────────

export const {
  focusMyHubCarouselTile,
  focusMyHubSavedItem,
  focusMyHubBulkAction,
  focusMyHubSeriesAction,
  focusMyHubSeriesItem,
  seedMyHubScheduledSingle,
  setMyHubRecordingQuota,
  resetMyHubFocus,
  resetMyHubContent,
  resetMyHubState,
  persistMyHubState,
  setMyHubExiting,
  setMyHubScrollInstantly,
  setCarouselRefreshPending,
  resetMyHubSort,
  leaveMyHubSeriesScreen,
  setCarouselItemCounts,
  navigateMyHubLeft,
  navigateMyHubRight,
  navigateMyHubUp,
  navigateMyHubDown,
  navigateMyHubSeriesLeft,
  navigateMyHubSeriesRight,
  navigateMyHubSeriesUp,
  navigateMyHubSeriesDown
} = myHubSlice.actions;

// ── Filter / sort selection ──────────────────────────────────────────────────
//
// The four actions below are thunks, not bare reducers, because picking a tab
// or a sort order emits analytics (`itemSelected`, and a `viewedScreen` for the
// tab being left). A reducer has to be pure: Redux DevTools replay and
// StrictMode double-dispatch both re-run it, which would re-emit those events.
// The reducer keeps the state transition; the thunk owns the telemetry.
//
// Each keeps the name and payload its callers already dispatch. The underlying
// pure actions are exported as `…State` for reducer-level tests.

export const {
  focusMyHubFilterChip: focusMyHubFilterChipState,
  focusMyHubSortChip: focusMyHubSortChipState,
  applyMyHubFilter: applyMyHubFilterState,
  applyMyHubSort: applyMyHubSortState
} = myHubSlice.actions;

/** Applies the filter chip at `filterIndex`, reporting the tab change. */
export const focusMyHubFilterChip =
  (payload: { filterIndex: number; filterId: MyHubFilterId }) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const previousFilter: MyHubFilterId = getState().myHub.selectedFilter;
    dispatch(focusMyHubFilterChipState(payload));
    if (previousFilter === payload.filterId) {
      return;
    }
    logMyHubFilterSelected(previousFilter, payload.filterId);
  };

/** Applies the sort chip at `sortIndex`, reporting the sort change. */
export const focusMyHubSortChip =
  (payload: { sortIndex: number; sortId: MyHubSortId }) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const previousSort: MyHubSortId = getState().myHub.selectedSort;
    dispatch(focusMyHubSortChipState(payload));
    if (previousSort === payload.sortId) {
      return;
    }
    logMyHubSortSelected(getState().myHub.selectedFilter, payload.sortId);
  };

/** Commits the highlighted filter chip (D-pad OK), reporting the tab change. */
export const applyMyHubFilter =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const previousFilter: MyHubFilterId = getState().myHub.selectedFilter;
    dispatch(applyMyHubFilterState());
    const nextFilter: MyHubFilterId = getState().myHub.selectedFilter;
    if (previousFilter === nextFilter) {
      return;
    }
    logMyHubFilterSelected(previousFilter, nextFilter);
  };

/** Commits the highlighted sort chip (D-pad OK), reporting the sort change. */
export const applyMyHubSort =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const previousSort: MyHubSortId = getState().myHub.selectedSort;
    dispatch(applyMyHubSortState());
    const nextSort: MyHubSortId = getState().myHub.selectedSort;
    if (previousSort === nextSort) {
      return;
    }
    logMyHubSortSelected(getState().myHub.selectedFilter, nextSort);
  };

/**
 * Screen unmount: keeps the visit when the user drilled into an item — a
 * carousel tile or a flat-list (Saved/Deleted) row, either of which can open a
 * content screen or the player — and resets it on any other exit, BACK out of
 * the rows included.
 *
 * The tab strip / sort control are left out on purpose: nothing reachable from
 * there leaves My Hub for a content screen, so a leave with focus still up
 * there reads as a real exit (BACK, or the app navigating elsewhere) rather
 * than a round trip to come back from.
 *
 * A thunk rather than a decision the view makes: the cleanup runs after the
 * last render, so reading focus from the store here is what keeps it current.
 */
export const leaveMyHubScreen =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const { focusRegion, isExiting } = getState().myHub;
    const isDrillIn: boolean =
      focusRegion === MyHubFocusRegion.CAROUSEL_ITEMS ||
      focusRegion === MyHubFocusRegion.SAVED_LIST ||
      focusRegion === MyHubFocusRegion.BULK_ACTION;
    if (isDrillIn && !isExiting) {
      Log.d(TAG, "leaveMyHubScreen() — drill-in from the rows, persisting");
      DiagnosticLogger.log(
        DIAGNOSTIC_CATEGORY,
        "leaveMyHubScreen() — drill-in, persisting",
        DiagnosticSeverity.Debug,
        { event: "screen_unmount", reason: "drill_in" }
      );
      dispatch(persistMyHubState());
      return;
    }
    Log.d(TAG, "leaveMyHubScreen() — exit, resetting");
    DiagnosticLogger.log(
      DIAGNOSTIC_CATEGORY,
      "leaveMyHubScreen() — exit, resetting",
      DiagnosticSeverity.Debug,
      { event: "screen_unmount", reason: "exit" }
    );
    logMyHubScreenExit(getState().myHub.selectedFilter);
    dispatch(resetMyHubState());
  };

/** Options for {@link refreshMyHubAfterAction}. */
interface TRefreshMyHubAfterActionOptions {
  /**
   * Move the cursor to the active surface's first item once it reloads.
   *
   * Defaults to false — preserve wherever focus already is (a folder-action
   * button, the Deleted tab's bulk button, the Sort control, ...), which is
   * the right outcome for anything that is not itself a single item: neither
   * `fetchMyHubSeriesDetail.fulfilled` nor `fetchMyHubSavedList.fulfilled`
   * touches `focusRegion`, so simply not moving focus here is enough to leave
   * it exactly where the press came from. Item-scoped actions
   * (`performMyHubAction`) pass `true`: the row that was just acted on is
   * gone or changed, so there is nothing sensible left to stay on.
   *
   * Also marks {@link MyHubState.scrollInstantly}, so the screen's
   * scroll-to-focus effect snaps to that first item rather than animating —
   * a refresh should read as instant, not as the whole rail visibly
   * scrolling itself back to the start.
   */
  moveFocusToFirstItem?: boolean;
}

/**
 * Refreshes whatever My Hub surface is currently active — the open
 * manage-series/scheduled-series folder, the Saved/Deleted flat list, or
 * otherwise the carousel grid — in full, plus the recording quota.
 *
 * The one refresh every My Hub mutation runs through now (see
 * `performMyHubAction`/`performMyHubBulkAction` in `myHubActionHandlers.ts`),
 * regardless of which surface — a carousel tile via the recording-highlight
 * popup, a Saved/Deleted row, a manage-series/scheduled-series row, or a
 * folder-level/tab-level bulk button — the action was picked from: no more
 * working out which one row or list actually changed, just re-read whatever
 * is on screen.
 */
export const refreshMyHubAfterAction =
  (options: TRefreshMyHubAfterActionOptions = {}) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const moveFocusToFirstItem: boolean = options.moveFocusToFirstItem === true;
    const { series, selectedFilter, filters } = getState().myHub;
    dispatch(fetchMyHubRecordingQuota());

    if (series.unifiedCollectionId !== "") {
      if (moveFocusToFirstItem) {
        dispatch(setMyHubScrollInstantly(true));
        dispatch(focusMyHubSeriesItem({ itemIndex: 0 }));
      }
      void dispatch(fetchMyHubSeriesDetail(series.unifiedCollectionId));
      return;
    }

    if (isMyHubFlatListFilter(selectedFilter, filters.items)) {
      if (moveFocusToFirstItem) {
        dispatch(setMyHubScrollInstantly(true));
        dispatch(focusMyHubSavedItem({ itemIndex: 0 }));
      }
      void dispatch(fetchMyHubSavedList(selectedFilter));
      return;
    }

    if (moveFocusToFirstItem) {
      dispatch(setMyHubScrollInstantly(true));
      dispatch(focusMyHubCarouselTile({ carouselIndex: 0, itemIndex: 0 }));
    }
    void dispatch(fetchMyHubCarousels());
  };

/**
 * Manage-series/scheduled-series screen unmount, in place of dispatching
 * {@link leaveMyHubSeriesScreen} directly.
 *
 * Runs that reducer's usual cleanup first — same drill-in-vs-exit split, same
 * result. `leaveMyHubSeriesScreen` clears `series.unifiedCollectionId` back
 * to `""` on a real exit, so once it has run, {@link refreshMyHubAfterAction}
 * reads the landing screen's carousel tab as the active surface — which is
 * exactly what a Delete, Cancel Series Recordings or Delete All Recordings on
 * this visit flagged via {@link MyHubState.carouselRefreshPending}: the
 * carousel grid behind this folder is refetched and the cursor lands on its
 * first tile, instantly, instead of the landing screen coming back showing
 * whatever it had before the folder was opened. A drill-in leaves the flag
 * set, so it still fires whenever the visit eventually does end for real.
 */
export const exitMyHubSeriesScreen =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const { focusRegion, isExiting, carouselRefreshPending } = getState().myHub;
    const isDrillIn: boolean =
      focusRegion === MyHubFocusRegion.SAVED_LIST && !isExiting;

    dispatch(leaveMyHubSeriesScreen());

    if (isDrillIn || !carouselRefreshPending) {
      return;
    }

    Log.d(TAG, "exitMyHubSeriesScreen() — refreshing the carousel grid");
    dispatch(refreshMyHubAfterAction({ moveFocusToFirstItem: true }));
    dispatch(setCarouselRefreshPending(false));
  };

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectMyHubSelectedFilter = (
  state: UnityRootState
): MyHubFilterId => state.myHub.selectedFilter;

export const selectMyHubSelectedSort = (state: UnityRootState): MyHubSortId =>
  state.myHub.selectedSort;

/** The filter/tab strip fetched from GET /v1/myHub/myHubFilters. */
export const selectMyHubFilterOptions = (
  state: UnityRootState
): TMyHubFilterMeta[] => state.myHub.filters.items;

export const selectIsLoadingMyHubFilters = (state: UnityRootState): boolean =>
  state.myHub.filters.isLoading;

export const selectMyHubFiltersError = (state: UnityRootState): string =>
  state.myHub.filters.error;

/**
 * The carousels in the order the screen should draw them.
 *
 * No client-side re-sort: `fetchMyHubCarousels` sends the sort chip's
 * `orderBy` on every request — including a request fired only because the
 * chip changed — so the API response already carries the rows in the right
 * order for every tab, the same way the Saved/Deleted flat list works.
 */
export const selectMyHubCarousels = (
  state: UnityRootState
): TMyHubCarouselData[] => state.myHub.carouselsPagination.items;

/**
 * Row-level pagination state for the carousel grid — `items`, `totalCount`
 * (total ROWS across the grid) and `isLoading`/`error` — for callers deciding
 * whether to dispatch {@link loadMoreMyHubCarousels} (see
 * {@link canLoadMoreMyHubCarousels}).
 */
export const selectMyHubCarouselsPagination = (
  state: UnityRootState
): TMyHubCarouselPaginationState => state.myHub.carouselsPagination;

export const selectIsLoadingMyHubCarousels = (state: UnityRootState): boolean =>
  state.myHub.carouselsPagination.isLoading;

/**
 * True while a fetch that *replaces* the carousel grid is in flight over rows
 * that are already on screen — in practice the sort case, since a tab switch
 * clears the rows first (see `clearMyHubContentOnFilterChange`) and a cold open
 * has none to begin with.
 *
 * The exact question `List` asks before dimming its own stack, and asked here
 * for the same answer: rows the user may be mid-read of stay put and fade
 * rather than being swapped for placeholders. An append is excluded — paging
 * adds rows below without disturbing the ones above.
 */
export const selectIsRefreshingMyHubCarousels = (
  state: UnityRootState
): boolean => {
  const pagination: TMyHubCarouselPaginationState =
    state.myHub.carouselsPagination;
  return (
    pagination.isLoading &&
    !pagination.isLoadingMore &&
    pagination.items.length > 0
  );
};

/** Offset-paginated tile state for one My Hub carousel row, keyed by carouselId. */
export const selectMyHubTilePagination =
  (carouselId: string) =>
  (state: UnityRootState): TTilePaginationState | undefined =>
    state.myHub.tilePagination[carouselId];

/** Every My Hub row's tile pagination state, keyed by carouselId. */
export const selectAllMyHubTilePagination = (
  state: UnityRootState
): Record<string, TTilePaginationState> => state.myHub.tilePagination;

export const selectMyHubCarouselsError = (state: UnityRootState): string =>
  state.myHub.carouselsPagination.error;

export const selectMyHubRecordingQuota = (
  state: UnityRootState
): TMyHubRecordingQuota | null => state.myHub.recordingQuota;

export const selectMyHubFocusRegion = (
  state: UnityRootState
): MyHubFocusRegion => state.myHub.focusRegion;

export const selectMyHubFocusedFilterIndex = (state: UnityRootState): number =>
  state.myHub.focusedFilterIndex;

export const selectMyHubFocusedSortIndex = (state: UnityRootState): number =>
  state.myHub.focusedSortIndex;

export const selectMyHubFocusedCarouselIndex = (
  state: UnityRootState
): number => state.myHub.focusedCarouselIndex;

export const selectMyHubFocusedItemIndex = (state: UnityRootState): number =>
  state.myHub.focusedItemIndex;

export const selectMyHubCarouselScrollPositions = (
  state: UnityRootState
): number[] => state.myHub.carouselScrollPositions;

/** See {@link MyHubState.scrollInstantly}. */
export const selectMyHubScrollInstantly = (state: UnityRootState): boolean =>
  state.myHub.scrollInstantly;

// ── Flat-list (Saved / Deleted) tab selectors ──

export const selectMyHubSavedItems = (
  state: UnityRootState
): TMyHubSavedItem[] => state.myHub.savedList.items;

export const selectMyHubSavedSummary = (
  state: UnityRootState
): TMyHubSavedListSummary | null => state.myHub.savedList.summary;

export const selectIsLoadingMyHubSavedList = (state: UnityRootState): boolean =>
  state.myHub.savedList.isLoading;

export const selectIsLoadingMoreMyHubSavedList = (
  state: UnityRootState
): boolean => state.myHub.savedList.isLoadingMore;

export const selectMyHubSavedListError = (state: UnityRootState): string =>
  state.myHub.savedList.error;

/**
 * Id of the carousel backing the flat list, or `null` before the first
 * response — the signal the view uses to know whether it is safe to page a
 * further slice in yet (mirrors carousel row tiles gating on
 * `tilePagination[carouselId] !== undefined`).
 */
export const selectMyHubSavedListCarouselId = (
  state: UnityRootState
): string | null => state.myHub.savedList.carouselId;

export const selectMyHubSavedListResolvedCount = (
  state: UnityRootState
): number | null => state.myHub.savedList.resolvedCount;

export const selectMyHubFocusedSavedIndex = (state: UnityRootState): number =>
  state.myHub.focusedSavedIndex;

export const selectMyHubFocusedSavedActionIndex = (
  state: UnityRootState
): number => state.myHub.focusedSavedActionIndex;

/**
 * The tab-level bulk action the response offered, or `null` when it offered
 * none — the flat list then renders no button beside its summary line.
 */
export const selectMyHubSavedBulkAction = (
  state: UnityRootState
): TMyHubBulkActionType | null => {
  const summary: TMyHubSavedListSummary | null = state.myHub.savedList.summary;
  return summary === null ? null : summary.bulkAction;
};

/** A flat-list row plus the action the D-pad cursor sits on. */
export type TMyHubFocusedSavedAction = {
  item: TMyHubSavedItem;
  actionType: TMyHubActionType;
} | null;

/**
 * Resolves the row and action the cursor is on, or `null` when focus is
 * elsewhere or the focused row has no action under the cursor.
 *
 * The result function shared by {@link selectMyHubFocusedSavedAction} and
 * {@link selectMyHubFocusedSeriesAction}: both screens render the same rows in
 * the same region, so only the `items` array they read differs.
 */
const resolveFocusedRowAction = (
  focusRegion: MyHubFocusRegion,
  items: TMyHubSavedItem[],
  focusedIndex: number,
  focusedActionIndex: number
): TMyHubFocusedSavedAction => {
  if (focusRegion !== MyHubFocusRegion.SAVED_LIST) {
    return null;
  }

  const item: TMyHubSavedItem | undefined = items[focusedIndex];
  if (item === undefined) {
    return null;
  }

  const actionType: TMyHubActionType | undefined =
    item.availableActions[focusedActionIndex];
  if (actionType === undefined) {
    return null;
  }

  return { item, actionType };
};

// ── Manage-series screen selectors ──

/**
 * Stable empty array for {@link selectMyHubSeriesFolderActions}.
 *
 * `useSelector` compares by reference, so returning a fresh `[]` before the
 * response arrives would report a change on every store notification and
 * re-render the header without end.
 */
const EMPTY_FOLDER_ACTIONS: TMyHubBulkActionType[] = [];

export const selectMyHubSeriesItems = (
  state: UnityRootState
): TMyHubSavedItem[] => state.myHub.series.items;

export const selectMyHubSeriesSummary = (
  state: UnityRootState
): TMyHubSeriesSummary | null => state.myHub.series.summary;

export const selectIsLoadingMyHubSeries = (state: UnityRootState): boolean =>
  state.myHub.series.isLoading;

export const selectMyHubSeriesError = (state: UnityRootState): string =>
  state.myHub.series.error;

export const selectMyHubFocusedSeriesActionIndex = (
  state: UnityRootState
): number => state.myHub.focusedSeriesActionIndex;

/**
 * Folder-level buttons the response offered for the open collection, in its own
 * order. Empty when it offered none — the header then renders no button row.
 */
export const selectMyHubSeriesFolderActions = (
  state: UnityRootState
): TMyHubBulkActionType[] => {
  const summary: TMyHubSeriesSummary | null = state.myHub.series.summary;
  return summary === null ? EMPTY_FOLDER_ACTIONS : summary.folderActions;
};

/** The manage-series row plus the action the D-pad cursor sits on. */
export const selectMyHubFocusedSeriesAction: (
  state: UnityRootState
) => TMyHubFocusedSavedAction = createSelector(
  [
    (state: UnityRootState): MyHubFocusRegion => state.myHub.focusRegion,
    (state: UnityRootState): TMyHubSavedItem[] => state.myHub.series.items,
    (state: UnityRootState): number => state.myHub.focusedSavedIndex,
    (state: UnityRootState): number => state.myHub.focusedSavedActionIndex
  ],
  resolveFocusedRowAction
);

/**
 * The Saved row and action the D-pad is currently on, or `null` when focus is
 * elsewhere or the focused row has no action under the cursor.
 *
 * Exists so the screen's OK handler resolves the target in one place instead of
 * re-deriving it from three separate selectors at the call site.
 *
 * MUST stay memoized. It returns a composite object, and `useSelector` compares
 * results by reference — an unmemoized version hands back a new object on every
 * call, so the store subscription sees a change on every notification and the
 * component re-renders without end ("Maximum update depth exceeded"). Memoizing
 * on the four inputs below keeps the identity stable until focus or the rows
 * actually move.
 */
export const selectMyHubFocusedSavedAction: (
  state: UnityRootState
) => TMyHubFocusedSavedAction = createSelector(
  [
    (state: UnityRootState): MyHubFocusRegion => state.myHub.focusRegion,
    (state: UnityRootState): TMyHubSavedItem[] => state.myHub.savedList.items,
    (state: UnityRootState): number => state.myHub.focusedSavedIndex,
    (state: UnityRootState): number => state.myHub.focusedSavedActionIndex
  ],
  resolveFocusedRowAction
);

export default myHubSlice.reducer;

// ── Internal type alias (used only in this file) ──────────────────────────────

type TMyHubSortMeta = (typeof MY_HUB_SORT_OPTIONS)[number];
